import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, JSX } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { Layer, Line, Rect, Stage, Text, Group, Transformer, Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';
import Swal from 'sweetalert2';
import { nanoid } from 'nanoid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { db } from '@/lib/firebase';
import { isActiveRsvpData, isAttendeeCountingRsvp } from '@/lib/event-utils';
import { showError, showSuccess, showWarning, confirmAction } from '@/lib/swal';
import { describePlaceCardGenerationProblems, generateCustomPlaceCards } from '@/lib/pdf-generator';
import { loadEventTablePlanTables, subscribeEventTablePlanTables } from '@/lib/table-plan-seating';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import { expandMultiTenantGuestPlaceholders } from '@/lib/table-plan-guest-placeholders';
import {
  createDefaultPlaceCardTemplate,
  DEFAULT_PLACE_CARD_FRAME,
  loadEventPlaceCardTemplate,
  normalizePlaceCardTemplateDates,
  upgradePlaceCardTemplate,
} from '@/lib/event-place-card-template';
import {
  applyPlaceholders,
  buildPlaceCardSeatingMap,
  buildPlaceholderMap,
} from '@/lib/place-card-placeholders';
import { buildSpiralPoints, buildWavyRectPoints } from '@/lib/place-card-frame-geometry';
import { cssFontWeight, ensurePlaceCardFontsLoaded, PLACE_CARD_FONT_OPTIONS } from '@/lib/place-card-fonts';
import {
  MIN_CUSTOM_CARD_INCHES,
  PAPER_SIZE_INCHES,
  placeCardGrid,
  templateCardSizeInches,
} from '@/lib/place-card-layout';
import {
  isInlineImageUrl,
  MAX_PLACE_CARD_IMAGE_BYTES,
  migrateInlinePlaceCardImages,
  uploadPlaceCardImage,
} from '@/lib/place-card-images';
import type { PlaceCardImageScope } from '@/lib/place-card-images';
import {
  CardSize,
  Event as DiningEvent,
  PaperSize,
  PlaceCardElement,
  PlaceCardFrame,
  PlaceCardFrameStyle,
  PlaceCardPlaceholder,
  PlaceCardTemplate,
  RSVP,
  Table,
  Unit,
  UnitType,
  Rank,
} from '@/types';
import {
  AlignCenter,
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  AlignLeft,
  AlignRight,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Image as ImageIcon,
  Loader2,
  Printer,
  Save,
  Trash2,
  Type,
  Layers,
  GripVertical,
  Ratio,
} from 'lucide-react';

type AlignmentGuide = {
  orientation: 'horizontal' | 'vertical';
  position: number;
};

const PX_PER_INCH = 96;
const SNAP_THRESHOLD_PX = 8;

type FirestoreDateLike = Date | { toDate: () => Date } | null | undefined;

function resolveFirestoreDate(value: FirestoreDateLike, fallback?: Date): Date {
  if (value instanceof Date) {
    return value;
  }
  if (value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch (error) {
      console.warn('Failed to convert Firestore timestamp', error);
    }
  }
  return fallback ?? new Date();
}

const PLACEHOLDER_OPTIONS: Array<{
  token: PlaceCardPlaceholder;
  label: string;
  description: string;
}> = [
  { token: '{guest_full_name}', label: 'Guest Full Name', description: 'Full name as recorded on the RSVP' },
  { token: '{guest_first_name}', label: 'Guest First Name', description: 'First name only' },
  { token: '{guest_last_name}', label: 'Guest Last Name', description: 'Last name only' },
  { token: '{guest_prefix}', label: 'Guest Prefix (Short)', description: 'Short prefix or honorific (e.g. WBro, Bro)' },
  { token: '{guest_prefix_full}', label: 'Guest Prefix (Full)', description: 'Full prefix or honorific (e.g. Worshipful Brother, Brother)' },
  { token: '{guest_rank}', label: 'Guest Rank', description: 'Masonic rank or honours (e.g. ProvJGW, PPADoC)' },
  { token: '{guest_unit}', label: 'Guest Unit', description: 'Unit or lodge name' },
  { token: '{guest_meal}', label: 'Meal Choice', description: 'Selected meal or main course (comma-separated if several)' },
  {
    token: '{guest_meal_split}',
    label: 'Meal Choice (line break)',
    description: 'Same as meal choice, but each course option on its own line',
  },
  { token: '{guest_table}', label: 'Table Name', description: 'Table assignment' },
  { token: '{guest_seat}', label: 'Seat Number', description: 'Assigned seat number' },
  { token: '{event_name}', label: 'Event Name', description: 'Full event title' },
  { token: '{event_date}', label: 'Event Date', description: 'Event date in readable format' },
  { token: '{event_location}', label: 'Event Location', description: 'Event venue/location' },
  { token: '{unit_name}', label: 'Unit Name', description: 'Hosting unit name' },
  { token: '{unit_number}', label: 'Unit Number', description: 'Hosting unit number' },
];

function toRatio(value: number, total: number): number {
  if (total === 0) {
    return 0;
  }
  const ratio = value / total;
  return Math.min(1, Math.max(0, Number(ratio.toFixed(4))));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const MIN_ELEMENT_SIZE_PX = 20;

/**
 * Clamps an element's centre so both its stored (unrotated) box and its rotated footprint stay on
 * the card. If the element cannot fit along an axis it is centred on that axis.
 */
function clampElementCenter(
  centerX: number,
  centerY: number,
  widthPx: number,
  heightPx: number,
  rotationDeg: number,
  cardWidthPx: number,
  cardHeightPx: number
): { x: number; y: number } {
  const radians = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const halfX = Math.max(widthPx / 2, (widthPx * cos + heightPx * sin) / 2);
  const halfY = Math.max(heightPx / 2, (widthPx * sin + heightPx * cos) / 2);
  const clampAxis = (value: number, half: number, size: number) =>
    half * 2 >= size ? size / 2 : clamp(value, half, size - half);
  return {
    x: clampAxis(centerX, halfX, cardWidthPx),
    y: clampAxis(centerY, halfY, cardHeightPx),
  };
}

function inferTextElementName(content: string | undefined): string {
  return (content ?? '').replace(/\s+/g, ' ').trim().slice(0, 40) || 'Text Element';
}

type FrameRect = { x: number; y: number; width: number; height: number };

/**
 * Computes the top-panel frame rectangle plus its mirror in the bottom panel (reflected across
 * the fold line), so a single set of frame settings produces a matching border on both sides of
 * the folded card.
 */
function computeFramePanels(
  cardWidthPx: number,
  foldLineY: number,
  frame: PlaceCardFrame
): { top: FrameRect; bottom: FrameRect } | null {
  const topWidth = cardWidthPx - frame.padding * 2;
  const topHeight = foldLineY - frame.padding * 2;
  if (topWidth <= 0 || topHeight <= 0) {
    return null;
  }

  const top: FrameRect = {
    x: frame.padding + frame.offsetX,
    y: frame.padding + frame.offsetY,
    width: topWidth,
    height: topHeight,
  };

  const bottom: FrameRect = {
    x: top.x,
    y: 2 * foldLineY - (top.y + top.height),
    width: top.width,
    height: top.height,
  };

  return { top, bottom };
}

const FRAME_DASH_PATTERNS: Partial<Record<PlaceCardFrameStyle, (thickness: number) => number[]>> = {
  dashed: (thickness) => [thickness * 3, thickness * 2],
  dotted: (thickness) => [thickness, thickness * 1.6],
};

function DoubleFrame({ rect, frame, listening }: { rect: FrameRect; frame: PlaceCardFrame; listening: boolean }) {
  const gap = frame.thickness * 2;
  const innerX = rect.x + frame.thickness + gap;
  const innerY = rect.y + frame.thickness + gap;
  const innerWidth = rect.width - (frame.thickness + gap) * 2;
  const innerHeight = rect.height - (frame.thickness + gap) * 2;
  return (
    <>
      <Rect
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        stroke={frame.color}
        strokeWidth={frame.thickness}
        listening={listening}
      />
      {innerWidth > 0 && innerHeight > 0 && (
        <Rect
          x={innerX}
          y={innerY}
          width={innerWidth}
          height={innerHeight}
          stroke={frame.color}
          strokeWidth={frame.thickness}
          listening={listening}
        />
      )}
    </>
  );
}

/** Thin single-weight line with an open gap at each corner, like a modern museum placard. */
function ModernFrame({ rect, frame, listening }: { rect: FrameRect; frame: PlaceCardFrame; listening: boolean }) {
  const gap = Math.min(frame.thickness * 3, rect.width / 4, rect.height / 4);
  const { x, y, width, height } = rect;
  const segments: number[][] = [
    [x + gap, y, x + width - gap, y],
    [x + width, y + gap, x + width, y + height - gap],
    [x + width - gap, y + height, x + gap, y + height],
    [x, y + height - gap, x, y + gap],
  ];
  return (
    <>
      {segments.map((points, index) => (
        <Line
          key={index}
          points={points}
          stroke={frame.color}
          strokeWidth={frame.thickness}
          lineCap="square"
          listening={listening}
        />
      ))}
    </>
  );
}

/** Thin outer line plus a heavier inset line and diamond corner accents. */
function ElegantFrame({ rect, frame, listening }: { rect: FrameRect; frame: PlaceCardFrame; listening: boolean }) {
  const outerStroke = Math.max(1, frame.thickness * 0.6);
  const gap = frame.thickness * 2.2;
  const innerWidth = rect.width - gap * 2;
  const innerHeight = rect.height - gap * 2;
  const diamondSize = frame.thickness * 2.4;
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ];
  return (
    <>
      <Rect
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        stroke={frame.color}
        strokeWidth={outerStroke}
        listening={listening}
      />
      {innerWidth > 0 && innerHeight > 0 && (
        <Rect
          x={rect.x + gap}
          y={rect.y + gap}
          width={innerWidth}
          height={innerHeight}
          stroke={frame.color}
          strokeWidth={frame.thickness}
          listening={listening}
        />
      )}
      {corners.map((corner, index) => (
        <Rect
          key={index}
          x={corner.x}
          y={corner.y}
          width={diamondSize}
          height={diamondSize}
          offsetX={diamondSize / 2}
          offsetY={diamondSize / 2}
          fill={frame.color}
          rotation={45}
          listening={listening}
        />
      ))}
    </>
  );
}

/** Heavy rounded, roughly-dashed outer line with a thin inset accent, evoking a rope-bound wood frame. */
function RusticFrame({ rect, frame, listening }: { rect: FrameRect; frame: PlaceCardFrame; listening: boolean }) {
  const outerStroke = frame.thickness * 1.8;
  const cornerRadius = frame.thickness * 1.5;
  const innerInset = outerStroke + frame.thickness;
  const innerWidth = rect.width - innerInset * 2;
  const innerHeight = rect.height - innerInset * 2;
  return (
    <>
      <Rect
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        stroke={frame.color}
        strokeWidth={outerStroke}
        cornerRadius={cornerRadius}
        dash={[frame.thickness * 2.5, frame.thickness * 1.2]}
        lineCap="round"
        listening={listening}
      />
      {innerWidth > 0 && innerHeight > 0 && (
        <Rect
          x={rect.x + innerInset}
          y={rect.y + innerInset}
          width={innerWidth}
          height={innerHeight}
          stroke={frame.color}
          strokeWidth={Math.max(1, frame.thickness * 0.5)}
          listening={listening}
        />
      )}
    </>
  );
}

/** Wavy scalloped outline with an Archimedean-spiral scroll flourish nested in each corner. */
function RoyalFrame({ rect, frame, listening }: { rect: FrameRect; frame: PlaceCardFrame; listening: boolean }) {
  const amplitude = frame.thickness * 1.6;
  const targetWavelength = Math.max(frame.thickness * 8, 24);
  const waveFlatPoints = buildWavyRectPoints(rect.width, rect.height, amplitude, targetWavelength).flatMap(
    ([x, y]) => [x, y]
  );

  const curlSize = Math.min(rect.width, rect.height) * 0.22;
  const spiralFlatPoints = buildSpiralPoints(1.75, curlSize * 0.5, 40).flatMap(([x, y]) => [x, y]);
  const corners = [
    { x: rect.x, y: rect.y, rotation: 0 },
    { x: rect.x + rect.width, y: rect.y, rotation: 90 },
    { x: rect.x + rect.width, y: rect.y + rect.height, rotation: 180 },
    { x: rect.x, y: rect.y + rect.height, rotation: 270 },
  ];

  return (
    <>
      <Line
        x={rect.x}
        y={rect.y}
        points={waveFlatPoints}
        stroke={frame.color}
        strokeWidth={frame.thickness}
        tension={0.4}
        closed
        lineJoin="round"
        lineCap="round"
        listening={listening}
      />
      {corners.map((corner, index) => (
        <Line
          key={index}
          x={corner.x}
          y={corner.y}
          rotation={corner.rotation}
          points={spiralFlatPoints}
          stroke={frame.color}
          strokeWidth={Math.max(1, frame.thickness * 0.6)}
          tension={0.5}
          lineCap="round"
          listening={listening}
        />
      ))}
    </>
  );
}

function FrameRectNode({ rect, frame, listening }: { rect: FrameRect; frame: PlaceCardFrame; listening: boolean }) {
  if (frame.style === 'double') {
    return <DoubleFrame rect={rect} frame={frame} listening={listening} />;
  }
  if (frame.style === 'modern') {
    return <ModernFrame rect={rect} frame={frame} listening={listening} />;
  }
  if (frame.style === 'elegant') {
    return <ElegantFrame rect={rect} frame={frame} listening={listening} />;
  }
  if (frame.style === 'royal') {
    return <RoyalFrame rect={rect} frame={frame} listening={listening} />;
  }
  if (frame.style === 'rustic') {
    return <RusticFrame rect={rect} frame={frame} listening={listening} />;
  }

  const dash = FRAME_DASH_PATTERNS[frame.style]?.(frame.thickness);
  const cornerRadius = frame.style === 'rounded' ? frame.cornerRadius : 0;

  return (
    <Rect
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      stroke={frame.color}
      strokeWidth={frame.thickness}
      cornerRadius={cornerRadius}
      dash={dash}
      lineCap={frame.style === 'dotted' ? 'round' : 'butt'}
      lineJoin={frame.style === 'dotted' ? 'round' : 'miter'}
      listening={listening}
    />
  );
}

function getImageNativeDimensions(image: HTMLImageElement): { width: number; height: number } | null {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (width > 0 && height > 0) {
    return { width, height };
  }
  return null;
}

function getPlaceCardElementNativeAspectRatio(
  element: PlaceCardElement,
  loadedImage?: HTMLImageElement | null,
): number | null {
  if (element.nativeAspectRatio && element.nativeAspectRatio > 0) {
    return element.nativeAspectRatio;
  }
  if (loadedImage) {
    const dims = getImageNativeDimensions(loadedImage);
    if (dims) {
      return dims.width / dims.height;
    }
  }
  return null;
}

function computeImageDimensionsForCard(
  naturalWidth: number,
  naturalHeight: number,
): { width: number; height: number; nativeAspectRatio: number } {
  const nativeAspectRatio = naturalWidth / naturalHeight;
  const maxWidth = 0.2;
  const maxHeight = 0.2;
  let width: number;
  let height: number;
  if (nativeAspectRatio >= maxWidth / maxHeight) {
    width = maxWidth;
    height = width / nativeAspectRatio;
  } else {
    height = maxHeight;
    width = height * nativeAspectRatio;
  }
  return { width, height, nativeAspectRatio };
}

/**
 * Number input that only commits on blur/Enter, so intermediate keystrokes (e.g. clearing the
 * field to type "12") don't resize the card and rescale every element along the way.
 */
function DraftNumberInput({
  value,
  min,
  max,
  step,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  step?: string;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Number(draft);
    if (!draft.trim() || !Number.isFinite(parsed)) {
      setDraft(String(value));
      return;
    }
    const next = Number(clamp(parsed, min, max).toFixed(2));
    setDraft(String(next));
    if (next !== value) {
      onCommit(next);
    }
  };

  return (
    <Input
      className="mt-1"
      type="number"
      step={step}
      min={min}
      max={max}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
        }
      }}
    />
  );
}

/** Matches synthetic RSVPs used for admin template preview/PDF samples. */
const ADMIN_PLATFORM_PREVIEW_EVENT_ID = 'admin-template-preview';

/** Example unit strings for placeholders when editing platform templates (real units use their own data at print time). */
const ADMIN_TEMPLATE_PREVIEW_UNIT_INFO = {
  name: 'Example Lodge',
  number: '123',
} as const;

function createAdminTemplatePreviewRsvps(): RSVP[] {
  const now = new Date();
  const mk = (
    id: string,
    first: string,
    last: string,
    rank: string,
    shortRank: string,
    additionalRank: string,
    unit: string,
    meal: string
  ): RSVP => ({
    id,
    eventId: ADMIN_PLATFORM_PREVIEW_EVENT_ID,
    userId: `preview-${id}`,
    name: `${first} ${last}`,
    firstName: first,
    lastName: last,
    rank,
    rankShortName: shortRank,
    additionalRank,
    unit,
    email: `${first.toLowerCase()}.${last.toLowerCase()}@example.com`,
    responses: { Meal: meal },
    status: 'confirmed',
    apologies: false,
    invoiceId: 'preview',
    paid: true,
    checkedIn: false,
    plusOneAllowed: false,
    createdAt: now,
    updatedAt: now,
  });

  return [
    mk('admin-preview-rsvp-1', 'John', 'Smith', 'W.Bro.', 'W.Bro', 'PPJGW', 'Example Lodge No. 123', 'Beef'),
    mk('admin-preview-rsvp-2', 'Jane', 'Doe', 'Bro.', 'Bro', 'PPADoC', 'Visitors Chapter No. 45', 'Salmon'),
    mk('admin-preview-rsvp-3', 'Robert', 'Johnson', 'Bro.', 'Bro', 'Past Master', 'Mother Lodge No. 1', 'Vegetarian'),
  ];
}

/** Synthetic event for placeholder preview when editing platform templates (admin app). */
function createMockAdminPreviewEvent(): DiningEvent {
  const now = new Date();
  return {
    id: ADMIN_PLATFORM_PREVIEW_EVENT_ID,
    unitId: '',
    name: 'All units',
    date: now,
    location: 'Admin template (shared with every unit)',
    questionnaire: [],
    currentAttendees: 0,
    waitlistEnabled: false,
    waitlist: [],
    rsvpDeadline: now,
    paymentDeadline: now,
    eventStatus: 'published',
    createdBy: 'system',
    createdAt: now,
    tickets: [],
    courses: [],
    requirePrefixRankUnitInfo: true,
  } as DiningEvent;
}

function createAdminPlatformDefaultTemplate(): PlaceCardTemplate {
  const now = new Date();
  return {
    id: '',
    unitId: '',
    eventId: undefined,
    name: 'Platform place card template',
    paperSize: 'A4',
    cardSize: '4x6',
    cardsPerPage: 2,
    customCardWidth: undefined,
    customCardHeight: undefined,
    elements: [],
    layout: { foldLinePosition: 0.5, showFoldLine: true, mirrorByDefault: false },
    createdAt: now,
    updatedAt: now,
    isAdminTemplate: true,
  };
}

/** Stable fingerprint of template design (excludes updatedAt so edits do not thrash on timestamp-only churn). */
function templateDesignFingerprint(template: PlaceCardTemplate | null): string {
  if (!template) {
    return '';
  }
  const { updatedAt, ...rest } = template;
  void updatedAt;
  return JSON.stringify(rest, (_, v) => (v instanceof Date ? v.toISOString() : v));
}

interface KonvaImageElementProps {
  width: number;
  height: number;
  image: HTMLImageElement | null;
  mirrored?: boolean;
}

function KonvaImageElement({
  width,
  height,
  image,
  mirrored = false,
}: KonvaImageElementProps) {
  // If no image loaded, show a placeholder
  if (!image) {
    return (
      <>
        <Rect
          width={width}
          height={height}
          fill="#f3f4f6"
          stroke="#d1d5db"
          strokeWidth={1}
          dash={[5, 5]}
          listening={!mirrored}
        />
        <Text
          text="Image not loaded"
          width={width}
          height={height}
          fontSize={Math.min(width, height) * 0.15}
          fill="#9ca3af"
          align="center"
          verticalAlign="middle"
          listening={!mirrored}
        />
      </>
    );
  }

  return (
    <KonvaImage
      image={image}
      width={width}
      height={height}
      listening={!mirrored}
    />
  );
}

interface SortableLayerItemProps {
  element: PlaceCardElement;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function SortableLayerItem({
  element,
  isSelected,
  onSelect,
  onDuplicate,
  onDelete,
}: SortableLayerItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const elementName = element.name ?? (element.type === 'text' ? 'Text Element' : 'Image Element');
  const elementType = element.type === 'text' ? 'Type' : 'ImageIcon';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-md border p-2 transition-colors ${
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:bg-muted'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        type="button"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 text-left text-sm font-medium"
      >
        <div className="flex items-center gap-2">
          {elementType === 'Type' ? (
            <Type className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="truncate">{elementName}</span>
        </div>
      </button>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title="Duplicate"
        >
          <Copy className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default function PlaceCardDesignerPage(): JSX.Element {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const location = useLocation();
  const isAdminPlatformMode = location.pathname === '/admin/place-card-templates';
  const navigate = useNavigate();
  
  const stageRef = useRef<Konva.Stage>(null);
  // State (not a ref) so the fit-to-container effect runs once the canvas area actually mounts
  const [canvasContainer, setCanvasContainer] = useState<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const transformerRef = useRef<Konva.Transformer>(null);
  const lastPersistedFingerprintRef = useRef<string | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [event, setEvent] = useState<DiningEvent | null>(null);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [template, setTemplate] = useState<PlaceCardTemplate | null>(null);
  // Latest values for the save queue, which runs outside React's render cycle
  const templateRef = useRef<PlaceCardTemplate | null>(null);
  templateRef.current = template;
  const eventRef = useRef<DiningEvent | null>(null);
  eventRef.current = event;
  const [existingTemplates, setExistingTemplates] = useState<PlaceCardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuide[]>([]);
  const [unitLogoUrl, setUnitLogoUrl] = useState<string | null>(null);
  const [unitLogoImage, setUnitLogoImage] = useState<HTMLImageElement | null>(null);
  const [customImages, setCustomImages] = useState<Record<string, { url: string; image: HTMLImageElement }>>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fontsVersion, setFontsVersion] = useState(0);
  const requestedImageUrlsRef = useRef<Record<string, string>>({});
  const [selectedTemplateFilter, setSelectedTemplateFilter] = useState<'all' | 'mine'>('all');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewCursor, setPreviewCursor] = useState(0);
  const contentTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [pendingPlaceholder, setPendingPlaceholder] = useState<PlaceCardPlaceholder | ''>('');
  const isTransformingRef = useRef(false);
  const [unitTypeRanks, setUnitTypeRanks] = useState<Rank[]>([]);

  const seatingMap = useMemo(() => buildPlaceCardSeatingMap(tables), [tables]);

  // Multi-tenant bookings get a "Guest of …" card for each ticket after the first, as seated in the planner.
  const cardRsvps = useMemo(
    () => (isMultiTenantEvent(event) ? expandMultiTenantGuestPlaceholders(rsvps) : rsvps),
    [event, rsvps]
  );

  // Only attending guests get cards, not apologies or cancelled bookings (admin editor uses sample guests like a real event)
  const previewGuests = useMemo(() => {
    const source = isAdminPlatformMode ? createAdminTemplatePreviewRsvps() : cardRsvps;
    return source.filter(isAttendeeCountingRsvp);
  }, [isAdminPlatformMode, cardRsvps]);

  const preparedUnitInfo = useMemo(
    () =>
      isAdminPlatformMode
        ? {
            name: ADMIN_TEMPLATE_PREVIEW_UNIT_INFO.name,
            number: ADMIN_TEMPLATE_PREVIEW_UNIT_INFO.number,
            logoUrl: unitLogoUrl ?? undefined,
          }
        : {
            name: unit?.name ?? '',
            number: unit?.unitNumber ?? unit?.settings?.defaultEventSettings?.unitId ?? '',
            logoUrl: unitLogoUrl ?? undefined,
          },
    [
      isAdminPlatformMode,
      unit?.name,
      unit?.unitNumber,
      unit?.settings?.defaultEventSettings?.unitId,
      unitLogoUrl,
    ]
  );

  useEffect(() => {
    if (previewCursor >= previewGuests.length && previewGuests.length > 0) {
      setPreviewCursor(0);
    }
  }, [previewCursor, previewGuests.length]);

  useEffect(() => {
    if (previewModalOpen && previewGuests.length > 0) {
      setPreviewCursor(0);
    }
  }, [previewModalOpen, previewGuests.length]);

  useEffect(() => {
    setPendingPlaceholder('');
  }, [selectedElementId]);

  const previewModalGuest = useMemo(
    () => (previewGuests.length > 0 ? previewGuests[Math.min(previewCursor, previewGuests.length - 1)] ?? null : null),
    [previewGuests, previewCursor]
  );

  const openPreviewModal = useCallback(() => {
    if (!template) {
      return;
    }
    setPreviewCursor(0);
    setPreviewModalOpen(true);
  }, [template]);

  const handlePreviewModalNext = useCallback(() => {
    if (previewGuests.length === 0) {
      return;
    }
    setPreviewCursor((prev) => (prev + 1) % previewGuests.length);
  }, [previewGuests.length]);

  const handlePreviewModalPrevious = useCallback(() => {
    if (previewGuests.length === 0) {
      return;
    }
    setPreviewCursor((prev) => (prev - 1 + previewGuests.length) % previewGuests.length);
  }, [previewGuests.length]);

  const cardDimensions = useMemo(() => {
    if (!template) {
      return { widthInches: 4, heightInches: 6, widthPx: 4 * PX_PER_INCH, heightPx: 6 * PX_PER_INCH };
    }
    const { width, height } = templateCardSizeInches(template);
    return {
      widthInches: width,
      heightInches: height,
      widthPx: width * PX_PER_INCH,
      heightPx: height * PX_PER_INCH,
    };
  }, [template]);

  const cardGrid = useMemo(
    () => placeCardGrid(template?.paperSize ?? 'A4', cardDimensions.widthInches, cardDimensions.heightInches),
    [template?.paperSize, cardDimensions.widthInches, cardDimensions.heightInches]
  );

  const foldLineY = useMemo(() => {
    // Always use centre fold position in preview
    return cardDimensions.heightPx * 0.5;
  }, [cardDimensions.heightPx]);

  const framePanels = useMemo(() => {
    const frame = template?.layout?.frame;
    if (!frame?.enabled) {
      return null;
    }
    return computeFramePanels(cardDimensions.widthPx, foldLineY, frame);
  }, [template?.layout?.frame, cardDimensions.widthPx, foldLineY]);

  const renderFrame = useCallback(
    (listening: boolean) => {
      const frame = template?.layout?.frame;
      if (!frame?.enabled || !framePanels) {
        return null;
      }
      return (
        <>
          <FrameRectNode rect={framePanels.top} frame={frame} listening={listening} />
          <FrameRectNode rect={framePanels.bottom} frame={frame} listening={listening} />
        </>
      );
    },
    [template?.layout?.frame, framePanels]
  );

  useEffect(() => {
    if (!unitLogoUrl) {
      setUnitLogoImage(null);
      return;
    }

    let cancelled = false;
    const loaders: HTMLImageElement[] = [];
    const load = (useCors: boolean) => {
      const image = new Image();
      if (useCors) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = () => {
        if (!cancelled) {
          setUnitLogoImage(image);
        }
      };
      image.onerror = () => {
        if (cancelled) {
          return;
        }
        if (useCors) {
          // CORS not configured on the bucket: the canvas only displays the logo, so retry without it.
          // (The PDF export still needs CORS; see md/FIREBASE_STORAGE_CORS_SETUP.md.)
          load(false);
          return;
        }
        console.error('Failed to load unit logo image');
        setUnitLogoImage(null);
        showError('The unit logo could not be loaded. Check the logo in your unit settings.');
      };
      image.src = unitLogoUrl;
      loaders.push(image);
    };
    load(true);

    return () => {
      cancelled = true;
      loaders.forEach((image) => {
        image.onload = null;
        image.onerror = null;
      });
    };
  }, [unitLogoUrl]);

  useEffect(() => {
    if (!template?.elements) {
      return;
    }

    for (const el of template.elements) {
      if (el.type !== 'image' || el.imageType !== 'custom' || !el.imageUrl) {
        continue;
      }
      const requestedUrl = requestedImageUrlsRef.current[el.id];
      if (requestedUrl === el.imageUrl) {
        continue;
      }
      // Inline data URLs migrated to Storage are the same picture; keep the decoded copy
      if (requestedUrl && isInlineImageUrl(requestedUrl) && !isInlineImageUrl(el.imageUrl)) {
        requestedImageUrlsRef.current[el.id] = el.imageUrl;
        continue;
      }

      const imageUrl = el.imageUrl;
      requestedImageUrlsRef.current[el.id] = imageUrl;
      const img = new Image();
      if (!isInlineImageUrl(imageUrl)) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => {
        if (requestedImageUrlsRef.current[el.id] !== imageUrl) {
          return;
        }
        setCustomImages((prev) => ({ ...prev, [el.id]: { url: imageUrl, image: img } }));
      };
      img.onerror = () => {
        if (img.crossOrigin) {
          // Bucket CORS not configured: the canvas only displays the image, so retry without CORS
          img.removeAttribute('crossorigin');
          img.src = imageUrl;
          return;
        }
        console.warn('Failed to load place card image', el.id);
      };
      img.src = imageUrl;
    }
  }, [template?.elements]);

  // Load the web fonts the design uses, then re-measure text once they arrive
  const fontFacesKey = useMemo(
    () =>
      Array.from(
        new Set(
          (template?.elements ?? [])
            .filter((el) => el.type === 'text')
            .map((el) => `${el.fontFamily ?? ''}|${el.fontWeight ?? ''}|${el.fontStyle ?? ''}`)
        )
      )
        .sort()
        .join(';'),
    [template?.elements]
  );
  useEffect(() => {
    if (!fontFacesKey) {
      return;
    }
    let cancelled = false;
    void ensurePlaceCardFontsLoaded(templateRef.current?.elements ?? []).then(() => {
      if (!cancelled) {
        setFontsVersion((version) => version + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fontFacesKey]);

  const resolveElementImage = useCallback(
    (element: PlaceCardElement): HTMLImageElement | null => {
      if (element.type !== 'image') {
        return null;
      }
      if (element.imageType === 'custom') {
        return customImages[element.id]?.image ?? null;
      }
      return unitLogoImage;
    },
    [customImages, unitLogoImage],
  );

  const loadEvent = useCallback(async () => {
    if (!unitId || !eventId) {
      return;
    }

    const eventRef = doc(db, 'units', unitId, 'events', eventId);
    const eventSnap = await getDoc(eventRef);
    if (!eventSnap.exists()) {
      throw new Error('Event not found');
    }

    const data = eventSnap.data() as DiningEvent & {
      date?: FirestoreDateLike;
      rsvpDeadline?: FirestoreDateLike;
      paymentDeadline?: FirestoreDateLike;
      createdAt?: FirestoreDateLike;
    };
    const resolvedDate = resolveFirestoreDate(data.date);
    const eventData: DiningEvent = {
      ...data,
      id: eventSnap.id,
      date: resolvedDate,
      rsvpDeadline: resolveFirestoreDate(data.rsvpDeadline),
      paymentDeadline: resolveFirestoreDate(data.paymentDeadline) || resolvedDate,
      createdAt: resolveFirestoreDate(data.createdAt),
      requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
    } as DiningEvent;

    setEvent(eventData);
  }, [eventId, unitId]);

  const loadUnit = useCallback(async () => {
    if (!unitId) {
      return;
    }

    const unitRef = doc(db, 'units', unitId);
    const unitSnap = await getDoc(unitRef);
    if (!unitSnap.exists()) {
      throw new Error('Unit not found');
    }

    const data = unitSnap.data() as Unit & {
      createdAt?: FirestoreDateLike;
      updatedAt?: FirestoreDateLike;
    };
    const unitData: Unit = {
      ...data,
      id: unitSnap.id,
      createdAt: resolveFirestoreDate(data.createdAt),
      settings: {
        ...(data.settings ?? {}),
      },
    };

    setUnit(unitData);
    setUnitLogoUrl(unitData.settings?.logoUrl ?? null);

    // Fetch unit type and its ranks
    if (unitData.typeId) {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data();
          const unitTypes: UnitType[] = settingsData.unitTypes || [];
          const unitType = unitTypes.find((ut) => ut.id === unitData.typeId);
          if (unitType && unitType.ranks) {
            // Sort ranks by order
            const sortedRanks = [...unitType.ranks].sort((a, b) => a.order - b.order);
            setUnitTypeRanks(sortedRanks);
          } else {
            setUnitTypeRanks([]);
          }
        } else {
          setUnitTypeRanks([]);
        }
      } catch (error) {
        console.error('Error fetching unit type ranks:', error);
        setUnitTypeRanks([]);
      }
    } else {
      setUnitTypeRanks([]);
    }
  }, [unitId]);

  const loadRsvps = useCallback(async () => {
    if (!unitId || !eventId) {
      return;
    }

    const rsvpSnapshot = await getDocs(collection(db, 'units', unitId, 'events', eventId, 'rsvps'));
    const guests: RSVP[] = [];

    rsvpSnapshot.forEach((docSnap) => {
      const data = docSnap.data() as RSVP & {
        createdAt?: FirestoreDateLike;
        updatedAt?: FirestoreDateLike;
        checkInTime?: FirestoreDateLike;
      };
      if (!isActiveRsvpData(data)) {
        return;
      }
      guests.push({
        ...data,
        id: docSnap.id,
        createdAt: resolveFirestoreDate(data.createdAt),
        updatedAt: resolveFirestoreDate(data.updatedAt),
        checkInTime: data.checkInTime ? resolveFirestoreDate(data.checkInTime) : undefined,
      } as RSVP);
    });

    setRsvps(guests);
  }, [eventId, unitId]);

  const loadTables = useCallback(async () => {
    if (!unitId || !eventId) {
      return;
    }

    try {
      // Table planner floor plans are the source of truth for table/seat numbers.
      const planTables = await loadEventTablePlanTables(unitId, eventId);
      setTables(planTables);
    } catch (error) {
      console.error('Failed to load table plan for place cards', error);
      setTables([]);
    }
  }, [eventId, unitId]);

  const loadExistingTemplates = useCallback(async () => {
    if (isAdminPlatformMode) {
      setLoadingTemplates(true);
      try {
        const adminTemplatesSnap = await getDocs(collection(db, 'adminPlaceCardTemplates'));
        const adminTemplates = adminTemplatesSnap.docs.map((docSnap) => {
          const data = docSnap.data() as PlaceCardTemplate & {
            createdAt?: FirestoreDateLike;
            updatedAt?: FirestoreDateLike;
          };
          return upgradePlaceCardTemplate(
            normalizePlaceCardTemplateDates({
              ...data,
              id: docSnap.id,
              isAdminTemplate: true,
            })
          );
        });
        adminTemplates.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
        setExistingTemplates(adminTemplates);
      } catch (error) {
        console.error('Error loading admin place card templates', error);
        showError('Failed to load templates');
      } finally {
        setLoadingTemplates(false);
      }
      return;
    }

    if (!unitId) {
      return;
    }

    setLoadingTemplates(true);
    try {
      const unitTemplatesRef = collection(db, 'units', unitId, 'placeCardTemplates');
      const unitTemplatesSnap = await getDocs(unitTemplatesRef);
      const adminTemplatesSnap = await getDocs(collection(db, 'adminPlaceCardTemplates'));

      const unitTemplates = unitTemplatesSnap.docs.map((docSnap) => {
        const data = docSnap.data() as PlaceCardTemplate & {
          createdAt?: FirestoreDateLike;
          updatedAt?: FirestoreDateLike;
        };
        const withId = {
          ...data,
          id: docSnap.id,
        };
        return upgradePlaceCardTemplate(normalizePlaceCardTemplateDates(withId));
      });

      const adminTemplates = adminTemplatesSnap.docs.map((docSnap) => {
        const data = docSnap.data() as PlaceCardTemplate & {
          createdAt?: FirestoreDateLike;
          updatedAt?: FirestoreDateLike;
        };
        const withId = {
          ...data,
          id: docSnap.id,
          isAdminTemplate: true,
        };
        return upgradePlaceCardTemplate(normalizePlaceCardTemplateDates(withId));
      });

      const combined = [...adminTemplates, ...unitTemplates].sort((a, b) => {
        return b.updatedAt.getTime() - a.updatedAt.getTime();
      });

      setExistingTemplates(combined);
    } catch (error) {
      console.error('Error loading place card templates', error);
      showError('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [isAdminPlatformMode, unitId]);

  const loadEventTemplate = useCallback(async () => {
    if (isAdminPlatformMode) {
      return;
    }
    if (!unitId || !event) {
      return;
    }

    try {
      const next = await loadEventPlaceCardTemplate(unitId, event);
      lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
      setTemplate(next);
    } catch (error) {
      console.error('Error loading event place card template', error);
      showError('Failed to load current template');
      const next = createDefaultPlaceCardTemplate(event, unitId);
      lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
      setTemplate(next);
    }
  }, [isAdminPlatformMode, event, unitId]);

  const loadAdminPlatformTemplate = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'adminPlaceCardTemplates'));
      const templates = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as PlaceCardTemplate & {
          createdAt?: FirestoreDateLike;
          updatedAt?: FirestoreDateLike;
        };
        return upgradePlaceCardTemplate(
          normalizePlaceCardTemplateDates({
            ...data,
            id: docSnap.id,
            isAdminTemplate: true,
          })
        );
      });
      const sorted = [...templates].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      const next = sorted[0] ?? createAdminPlatformDefaultTemplate();
      lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
      setTemplate(next);
    } catch (error) {
      console.error('Error loading admin place card template', error);
      showError('Failed to load platform template');
      const next = createAdminPlatformDefaultTemplate();
      lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
      setTemplate(next);
    }
  }, []);

  const markSaved = useCallback(() => {
    setAutoSaveStatus('saved');
    if (savedStatusTimerRef.current) {
      clearTimeout(savedStatusTimerRef.current);
    }
    savedStatusTimerRef.current = setTimeout(() => {
      savedStatusTimerRef.current = null;
      setAutoSaveStatus('idle');
    }, 2000);
  }, []);

  /**
   * Writes the latest template (read from templateRef when the save actually runs, so queued saves
   * never write stale data or create a second document for a template the first save just created).
   */
  const persistLatestTemplate = useCallback(
    async (mode: 'auto' | 'manual'): Promise<'created' | 'updated' | 'skipped'> => {
      let snapshot = templateRef.current;
      const currentEvent = eventRef.current;
      if (!snapshot || !currentEvent) {
        return 'skipped';
      }
      if (!isAdminPlatformMode && !unitId) {
        return 'skipped';
      }
      if (mode === 'auto') {
        if (templateDesignFingerprint(snapshot) === lastPersistedFingerprintRef.current) {
          return 'skipped';
        }
        if (!isAdminPlatformMode && snapshot.eventId !== currentEvent.id) {
          return 'skipped';
        }
        setAutoSaveStatus('saving');
      }

      const imageScope: PlaceCardImageScope = isAdminPlatformMode
        ? { kind: 'admin' }
        : { kind: 'unit', unitId: unitId! };
      const migration = await migrateInlinePlaceCardImages(snapshot, imageScope);
      if (Object.keys(migration.replaced).length > 0) {
        snapshot = migration.template;
        const applyMigratedUrls = (tpl: PlaceCardTemplate | null) =>
          tpl
            ? {
                ...tpl,
                elements: (tpl.elements ?? []).map((el) =>
                  migration.replaced[el.id] && isInlineImageUrl(el.imageUrl)
                    ? { ...el, imageUrl: migration.replaced[el.id] }
                    : el
                ),
              }
            : tpl;
        templateRef.current = applyMigratedUrls(templateRef.current);
        setTemplate(applyMigratedUrls);
      }

      const isEventTemplate = !isAdminPlatformMode && snapshot.eventId === currentEvent.id;
      const payload = prepareTemplateForSave(
        isAdminPlatformMode
          ? { ...snapshot, updatedAt: new Date(), isAdminTemplate: true, unitId: '', eventId: undefined }
          : {
              ...snapshot,
              unitId: unitId!,
              isAdminTemplate: false,
              updatedAt: new Date(),
              ...(isEventTemplate ? { isDefault: true } : {}),
            }
      );
      const templatesRef = isAdminPlatformMode
        ? collection(db, 'adminPlaceCardTemplates')
        : collection(db, 'units', unitId!, 'placeCardTemplates');

      if (snapshot.id) {
        await updateDoc(doc(templatesRef, snapshot.id), payload);
        lastPersistedFingerprintRef.current = templateDesignFingerprint(snapshot);
        return 'updated';
      }

      const docRef = await addDoc(templatesRef, { ...payload, createdAt: new Date() });
      // Only the snapshot that was written counts as persisted; edits made while saving stay dirty.
      lastPersistedFingerprintRef.current = templateDesignFingerprint({ ...snapshot, id: docRef.id });
      const attachId = (tpl: PlaceCardTemplate | null) => (tpl && !tpl.id ? { ...tpl, id: docRef.id } : tpl);
      templateRef.current = attachId(templateRef.current);
      setTemplate(attachId);
      return 'created';
    },
    [isAdminPlatformMode, unitId]
  );

  const enqueueSave = useCallback(
    (mode: 'auto' | 'manual') => {
      const run = saveQueueRef.current.then(() => persistLatestTemplate(mode));
      saveQueueRef.current = run.catch(() => undefined);
      return run;
    },
    [persistLatestTemplate]
  );

  const runAutoSave = useCallback(async () => {
    try {
      const result = await enqueueSave('auto');
      if (result !== 'skipped') {
        markSaved();
      }
    } catch (error) {
      console.error('Failed to auto-save place card template', error);
      setAutoSaveStatus('error');
    }
  }, [enqueueSave, markSaved]);

  useEffect(() => {
    if (isAdminPlatformMode) {
      setEvent(createMockAdminPreviewEvent());
      setUnit(null);
      setUnitLogoUrl(null);
      setRsvps([]);
      setTables([]);
      setUnitTypeRanks([]);
      setLoading(false);
      return;
    }

    if (!unitId || !eventId) {
      setLoadError('This link is missing the unit or event.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    Promise.all([loadEvent(), loadUnit(), loadRsvps(), loadTables()])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to initialise place card designer', error);
        setLoadError(
          error instanceof Error && /not found/i.test(error.message)
            ? `${error.message}. It may have been deleted, or you may not have access.`
            : 'The event could not be loaded. Check your connection and try again.'
        );
        setLoading(false);
      });
  }, [isAdminPlatformMode, eventId, unitId, loadEvent, loadUnit, loadRsvps, loadTables]);

  // Keep seating in sync with live table planner edits
  useEffect(() => {
    if (isAdminPlatformMode || !unitId || !eventId) {
      return;
    }

    return subscribeEventTablePlanTables(unitId, eventId, setTables, (error) => {
      // Background listener only; the initial load still works if this fails
      console.error('Table plan listener failed for place cards', error);
    });
  }, [isAdminPlatformMode, unitId, eventId]);

  // Admin mode: load once. Deliberately independent of `event` (the example event is set after
  // mount), otherwise the platform template would be reloaded over the admin's first edits.
  useEffect(() => {
    if (!isAdminPlatformMode) {
      return;
    }
    void loadExistingTemplates();
    void loadAdminPlatformTemplate();
  }, [isAdminPlatformMode, loadExistingTemplates, loadAdminPlatformTemplate]);

  useEffect(() => {
    if (isAdminPlatformMode || !event || !unitId) {
      return;
    }
    void loadExistingTemplates();
    void loadEventTemplate();
  }, [isAdminPlatformMode, event, unitId, loadExistingTemplates, loadEventTemplate]);

  useEffect(() => {
    if (!template || !event) {
      return;
    }
    if (templateDesignFingerprint(template) === lastPersistedFingerprintRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      void runAutoSave();
    }, 2000);
    return () => clearTimeout(timer);
  }, [template, event, runAutoSave]);

  // Flush unsaved edits when the tab is hidden or the designer is closed
  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden') {
        void runAutoSave();
      }
    };
    document.addEventListener('visibilitychange', flush);
    return () => document.removeEventListener('visibilitychange', flush);
  }, [runAutoSave]);

  const runAutoSaveRef = useRef(runAutoSave);
  runAutoSaveRef.current = runAutoSave;
  useEffect(() => {
    return () => {
      void runAutoSaveRef.current();
      if (savedStatusTimerRef.current) {
        clearTimeout(savedStatusTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) {
      return;
    }

    if (!selectedElementId) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
      return;
    }

    const node = stageRef.current.findOne(`#element-${selectedElementId}`);
    if (node) {
      transformerRef.current.nodes([node as Konva.Node]);
      transformerRef.current.getLayer()?.batchDraw();
    } else {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedElementId, template, cardDimensions]);

  // Auto-scale canvas to fill available whitespace while preserving aspect ratio
  useEffect(() => {
    if (!canvasContainer) {
      return;
    }

    const updateScale = () => {
      const styles = window.getComputedStyle(canvasContainer);
      const availableWidth =
        canvasContainer.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      const availableHeight =
        canvasContainer.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
      if (availableWidth <= 0 || availableHeight <= 0) {
        return;
      }
      // Fit to width and to 90% of height (leave comfortable top/bottom space)
      const scaleX = availableWidth / cardDimensions.widthPx;
      const scaleY = (availableHeight / cardDimensions.heightPx) * 0.9;
      const next = Math.max(0.1, Math.min(scaleX, scaleY));
      setCanvasScale((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(canvasContainer);
    return () => observer.disconnect();
  }, [canvasContainer, cardDimensions.widthPx, cardDimensions.heightPx]);

  const handleManualSave = useCallback(async () => {
    if (!templateRef.current) {
      return;
    }

    setSavingTemplate(true);
    try {
      const result = await enqueueSave('manual');
      if (result === 'skipped') {
        return;
      }
      if (isAdminPlatformMode) {
        showSuccess(result === 'created' ? 'Template created for all units' : 'Template saved for all units');
      } else {
        showSuccess(result === 'created' ? 'Template created' : 'Template saved');
      }
      markSaved();
      void loadExistingTemplates();
    } catch (error) {
      console.error('Failed to save template', error);
      showError('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  }, [enqueueSave, isAdminPlatformMode, loadExistingTemplates, markSaved]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (!template) {
      return;
    }
    if (!isAdminPlatformMode && !unitId) {
      return;
    }

    const result = await Swal.fire({
      title: 'Save Template As',
      input: 'text',
      inputLabel: 'Template Name',
      inputValue: template.name,
      inputPlaceholder: 'Enter a template name',
      showCancelButton: true,
      confirmButtonText: 'Save',
      confirmButtonColor: '#1E3A5F',
      inputValidator: (value) => {
        if (!value) {
          return 'Template name is required';
        }
        return null;
      },
    });

    if (!result.isConfirmed || !result.value) {
      return;
    }

    setSavingTemplate(true);
    try {
      const newTemplate: PlaceCardTemplate = {
        ...template,
        id: '',
        name: result.value,
        isDefault: false,
        updatedAt: new Date(),
        createdAt: new Date(),
        ...(isAdminPlatformMode
          ? { eventId: undefined, unitId: '', isAdminTemplate: true }
          : { eventId: '', unitId: unitId!, isAdminTemplate: false }),
      };

      const templatesRef = isAdminPlatformMode
        ? collection(db, 'adminPlaceCardTemplates')
        : collection(db, 'units', unitId!, 'placeCardTemplates');
      const { template: storedTemplate } = await migrateInlinePlaceCardImages(
        newTemplate,
        isAdminPlatformMode ? { kind: 'admin' } : { kind: 'unit', unitId: unitId! }
      );
      await addDoc(templatesRef, prepareTemplateForSave(storedTemplate));
      showSuccess(isAdminPlatformMode ? 'Admin template saved for all units' : 'Template saved');
      loadExistingTemplates();
    } catch (error) {
      console.error('Failed to save template copy', error);
      showError('Failed to save template copy');
    } finally {
      setSavingTemplate(false);
    }
  }, [template, unitId, loadExistingTemplates, isAdminPlatformMode]);

  const handleDeleteTemplate = useCallback(
    async (templateId: string, isAdminTemplateDoc?: boolean) => {
      if (isAdminPlatformMode) {
        if (!isAdminTemplateDoc) {
          return;
        }

        const result = await confirmAction({
          title: 'Delete Template',
          text: 'Are you sure you want to delete this platform template?',
          confirmButtonText: 'Delete',
          confirmButtonColor: '#dc2626',
        });

        if (!result.isConfirmed) {
          return;
        }

        try {
          // Let any in-flight save finish first so it cannot recreate or update the deleted doc
          await saveQueueRef.current;
          await deleteDoc(doc(db, 'adminPlaceCardTemplates', templateId));
          showSuccess('Template deleted');
          setExistingTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));
          if (templateRef.current?.id === templateId) {
            const next = createAdminPlatformDefaultTemplate();
            lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
            templateRef.current = next;
            setTemplate(next);
            setSelectedElementId(null);
          }
        } catch (error) {
          console.error('Failed to delete template', error);
          showError('Failed to delete template');
        }
        return;
      }

      if (!unitId || isAdminTemplateDoc) {
        return;
      }

      const result = await confirmAction({
        title: 'Delete Template',
        text: 'Are you sure you want to delete this template?',
        confirmButtonText: 'Delete',
        confirmButtonColor: '#dc2626',
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await saveQueueRef.current;
        await deleteDoc(doc(db, 'units', unitId, 'placeCardTemplates', templateId));
        showSuccess('Template deleted');
        setExistingTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));
        if (templateRef.current?.id === templateId && event) {
          // Start a fresh, unsaved design; it is only written once the user edits it
          const next = createDefaultPlaceCardTemplate(event, unitId);
          lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
          templateRef.current = next;
          setTemplate(next);
          setSelectedElementId(null);
        }
      } catch (error) {
        console.error('Failed to delete template', error);
        showError('Failed to delete template');
      }
    },
    [unitId, event, isAdminPlatformMode]
  );

  const handleDuplicateTemplate = useCallback(
    async (templateToCopy: PlaceCardTemplate) => {
      if (isAdminPlatformMode) {
        setSavingTemplate(true);
        try {
          const duplicate: PlaceCardTemplate = {
            ...templateToCopy,
            id: '',
            unitId: '',
            eventId: undefined,
            name: `${templateToCopy.name} (Copy)`,
            isDefault: false,
            isAdminTemplate: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          const { template: storedDuplicate } = await migrateInlinePlaceCardImages(duplicate, { kind: 'admin' });
          await addDoc(collection(db, 'adminPlaceCardTemplates'), prepareTemplateForSave(storedDuplicate));
          showSuccess('Admin template copy saved for all units');
          loadExistingTemplates();
        } catch (error) {
          console.error('Failed to duplicate template', error);
          showError('Failed to duplicate template');
        } finally {
          setSavingTemplate(false);
        }
        return;
      }

      if (!unitId) {
        return;
      }

      setSavingTemplate(true);
      try {
        const duplicate: PlaceCardTemplate = {
          ...templateToCopy,
          id: '',
          unitId,
          eventId: '',
          name: `${templateToCopy.name} (Copy)`,
          isDefault: false,
          isAdminTemplate: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        const templatesRef = collection(db, 'units', unitId, 'placeCardTemplates');
        const { template: storedDuplicate } = await migrateInlinePlaceCardImages(duplicate, { kind: 'unit', unitId });
        await addDoc(templatesRef, prepareTemplateForSave(storedDuplicate));
        showSuccess('Template duplicated');
        loadExistingTemplates();
      } catch (error) {
        console.error('Failed to duplicate template', error);
        showError('Failed to duplicate template');
      } finally {
        setSavingTemplate(false);
      }
    },
    [unitId, loadExistingTemplates, isAdminPlatformMode]
  );

  const updateTemplate = useCallback((updates: Partial<PlaceCardTemplate>) => {
    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        ...updates,
        updatedAt: new Date(),
      };
    });
  }, []);

  /**
   * Applies paper/card size changes. Element boxes are stored as ratios so they follow the card,
   * but font sizes are absolute, so they are scaled with the card to keep text fitting its box.
   * Cards per page is clamped to what fits on the sheet.
   */
  const updateCardLayout = useCallback(
    (
      updates: Partial<Pick<PlaceCardTemplate, 'paperSize' | 'cardSize' | 'customCardWidth' | 'customCardHeight' | 'cardsPerPage'>>
    ) => {
      setTemplate((prev) => {
        if (!prev) {
          return prev;
        }
        const next = { ...prev, ...updates };
        const before = templateCardSizeInches(prev);
        const after = templateCardSizeInches(next);
        const { maxCardsPerPage } = placeCardGrid(next.paperSize, after.width, after.height);
        const fontScale = Math.min(after.width / before.width, after.height / before.height);
        const elements =
          Math.abs(fontScale - 1) < 0.001
            ? next.elements
            : (next.elements ?? []).map((el) =>
                el.type === 'text'
                  ? { ...el, fontSize: Math.max(6, Math.round((el.fontSize ?? 24) * fontScale * 2) / 2) }
                  : el
              );
        return {
          ...next,
          elements,
          cardsPerPage: clamp(Math.round(next.cardsPerPage || 1), 1, maxCardsPerPage),
          updatedAt: new Date(),
        };
      });
    },
    []
  );

  const updateFrame = useCallback((updates: Partial<PlaceCardFrame>) => {
    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      const nextFrame: PlaceCardFrame = {
        ...DEFAULT_PLACE_CARD_FRAME,
        ...prev.layout?.frame,
        ...updates,
      };
      return {
        ...prev,
        layout: {
          ...prev.layout,
          frame: nextFrame,
        },
        updatedAt: new Date(),
      };
    });
  }, []);

  const updateElement = useCallback((elementId: string, updates: Partial<PlaceCardElement>) => {
    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      const elements = prev.elements ?? [];
      return {
        ...prev,
        elements: elements.map((el) => (el.id === elementId ? { ...el, ...updates } : el)),
        updatedAt: new Date(),
      };
    });
  }, []);

  const removeElement = useCallback((elementId: string) => {
    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      const updatedElements = (prev.elements ?? []).filter((el) => el.id !== elementId);
      return {
        ...prev,
        elements: updatedElements,
        updatedAt: new Date(),
      };
    });
    setSelectedElementId((prev) => (prev === elementId ? null : prev));
  }, []);

  const reorderElements = useCallback((activeId: string, overId: string) => {
    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      const elements = prev.elements ?? [];
      const oldIndex = elements.findIndex((el) => el.id === activeId);
      const newIndex = elements.findIndex((el) => el.id === overId);

      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }

      // Since we display in reverse order (top layer first), when user drags in the UI,
      // we need to account for the visual vs actual array positions
      // The arrayMove function will correctly move the element, maintaining z-order
      return {
        ...prev,
        elements: arrayMove(elements, oldIndex, newIndex),
        updatedAt: new Date(),
      };
    });
  }, []);

  const duplicateElement = useCallback((elementId: string) => {
    if (!template) {
      return;
    }
    const elements = template.elements ?? [];
    const elementToDuplicate = elements.find((el) => el.id === elementId);
    if (!elementToDuplicate) {
      return;
    }

    const duplicated: PlaceCardElement = {
      ...elementToDuplicate,
      id: nanoid(10),
      x: (elementToDuplicate.x ?? 0) + 0.02, // Slight offset
      y: (elementToDuplicate.y ?? 0) + 0.02,
      name: `${elementToDuplicate.name ?? 'Element'} (Copy)`,
    };

    const elementIndex = elements.findIndex((el) => el.id === elementId);
    const newElements = [...elements];
    newElements.splice(elementIndex + 1, 0, duplicated);

    setTemplate((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        elements: newElements,
        updatedAt: new Date(),
      };
    });

    // Select the duplicated element
    setSelectedElementId(duplicated.id);
  }, [template]);

  const addTextElement = useCallback(() => {
    if (!template) {
      return;
    }

    const newElement: PlaceCardElement = {
      id: nanoid(10),
      type: 'text',
      name: 'Text Element',
      x: 0.1,
      y: 0.55,
      width: 0.8,
      height: 0.15,
      rotation: 0,
      opacity: 1,
      content: 'New Text',
      fontFamily: 'Georgia, serif',
      fontSize: 32,
      fontWeight: 'normal',
      textAlign: 'center',
      color: '#111827',
      letterSpacing: 0,
      lineHeight: 1.1,
      mirrored: template.layout?.mirrorByDefault ?? false,
      mirrorRotation: 180,
    };

    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        elements: [...(prev.elements ?? []), newElement],
        updatedAt: new Date(),
      };
    });
    setSelectedElementId(newElement.id);
  }, [template]);

  const addUnitLogoElement = useCallback(() => {
    if (!template || !unitLogoUrl) {
      return;
    }

    let width = 0.24;
    let height = 0.24;
    let nativeAspectRatio: number | undefined;

    if (unitLogoImage) {
      const nativeDims = getImageNativeDimensions(unitLogoImage);
      if (nativeDims) {
        const imageDims = computeImageDimensionsForCard(nativeDims.width, nativeDims.height);
        width = imageDims.width;
        height = imageDims.height;
        nativeAspectRatio = imageDims.nativeAspectRatio;
      }
    }

    const newElement: PlaceCardElement = {
      id: nanoid(10),
      type: 'image',
      name: 'Unit Logo',
      x: (1 - width) / 2,
      y: 0.12,
      width,
      height,
      rotation: 0,
      opacity: 1,
      imageType: 'unit-logo',
      imageUrl: unitLogoUrl,
      nativeAspectRatio,
      preserveAspectRatio: true,
      mirrored: false,
      mirrorRotation: 180,
    };

    setTemplate((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        elements: [...(prev.elements ?? []), newElement],
        updatedAt: new Date(),
      };
    });
    setSelectedElementId(newElement.id);
  }, [template, unitLogoUrl, unitLogoImage]);

  const handleImageUpload = useCallback(
    async (changeEvent: ChangeEvent<HTMLInputElement>) => {
      const input = changeEvent.target;
      const file = input.files?.[0];
      input.value = '';
      if (!file || !templateRef.current) {
        return;
      }
      if (!isAdminPlatformMode && !unitId) {
        return;
      }

      if (!file.type.startsWith('image/')) {
        showError('Please select a valid image file');
        return;
      }

      if (file.size > MAX_PLACE_CARD_IMAGE_BYTES) {
        showError('Image file is too large. Please select an image smaller than 10MB');
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      const loaded = await new Promise<boolean>((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = objectUrl;
      });
      if (!loaded) {
        URL.revokeObjectURL(objectUrl);
        showError('Failed to load image');
        return;
      }

      const dims = getImageNativeDimensions(img);
      if (!dims) {
        URL.revokeObjectURL(objectUrl);
        showError('Failed to read image dimensions');
        return;
      }

      setUploadingImage(true);
      let imageUrl: string;
      try {
        imageUrl = await uploadPlaceCardImage(
          file,
          isAdminPlatformMode ? { kind: 'admin' } : { kind: 'unit', unitId: unitId! }
        );
      } catch (error) {
        console.error('Failed to upload place card image', error);
        URL.revokeObjectURL(objectUrl);
        showError('Failed to upload image');
        return;
      } finally {
        setUploadingImage(false);
      }

      const imageDims = computeImageDimensionsForCard(dims.width, dims.height);
      const { width, height } = imageDims;
      const existingCustomImages = (templateRef.current?.elements ?? []).filter(
        (el) => el.type === 'image' && el.imageType === 'custom'
      ).length;

      const newElement: PlaceCardElement = {
        id: nanoid(10),
        type: 'image',
        name: `Image ${existingCustomImages + 1}`,
        x: (1 - width) / 2,
        y: (1 - height) / 2,
        width,
        height,
        rotation: 0,
        opacity: 1,
        imageType: 'custom',
        imageUrl,
        nativeAspectRatio: imageDims.nativeAspectRatio,
        preserveAspectRatio: true,
        mirrored: false,
        mirrorRotation: 180,
      };

      // The already-decoded local copy renders immediately; the Storage URL is what gets saved
      requestedImageUrlsRef.current[newElement.id] = imageUrl;
      setCustomImages((prev) => ({ ...prev, [newElement.id]: { url: imageUrl, image: img } }));
      setTemplate((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          elements: [...(prev.elements ?? []), newElement],
          updatedAt: new Date(),
        };
      });
      setSelectedElementId(newElement.id);
      showSuccess('Image added successfully');
    },
    [isAdminPlatformMode, unitId],
  );

  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (isTransformingRef.current) {
      e.cancelBubble = true;
      return;
    }
    // Element groups select themselves (and stop bubbling); keep the selection for clicks on the
    // transformer's anchors, rotater or border, and clear it for clicks on empty card space.
    let node: Konva.Node | null = e.target;
    while (node) {
      if (node === transformerRef.current || node.id().startsWith('element-')) {
        return;
      }
      node = node.getParent();
    }
    setSelectedElementId(null);
  }, []);

  const handleDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>, element: PlaceCardElement) => {
      const node = e.target as Konva.Group;
      const widthPx = node.width();
      const heightPx = node.height();
      const cardWidth = cardDimensions.widthPx;
      const cardHeight = cardDimensions.heightPx;
      // The group is offset by half its size, so node.x()/y() is the element's centre
      const bounded = clampElementCenter(node.x(), node.y(), widthPx, heightPx, node.rotation(), cardWidth, cardHeight);

      const verticalTargets = [cardWidth / 2];
      const horizontalTargets = [cardHeight / 2, foldLineY];
      (template?.elements ?? []).forEach((other) => {
        if (other.id === element.id) {
          return;
        }
        verticalTargets.push(((other.x ?? 0) + (other.width ?? 0.2) / 2) * cardWidth);
        horizontalTargets.push(((other.y ?? 0) + (other.height ?? 0.1) / 2) * cardHeight);
      });

      const snapTo = (value: number, targets: number[]) => {
        let best: number | null = null;
        for (const target of targets) {
          const distance = Math.abs(value - target);
          if (distance <= SNAP_THRESHOLD_PX && (best === null || distance < Math.abs(value - best))) {
            best = target;
          }
        }
        return best;
      };

      const guides: AlignmentGuide[] = [];
      let centerX = bounded.x;
      let centerY = bounded.y;
      const snapX = snapTo(centerX, verticalTargets);
      if (snapX !== null) {
        const snapped = clampElementCenter(snapX, centerY, widthPx, heightPx, node.rotation(), cardWidth, cardHeight);
        if (snapped.x === snapX) {
          centerX = snapX;
          guides.push({ orientation: 'vertical', position: snapX });
        }
      }
      const snapY = snapTo(centerY, horizontalTargets);
      if (snapY !== null) {
        const snapped = clampElementCenter(centerX, snapY, widthPx, heightPx, node.rotation(), cardWidth, cardHeight);
        if (snapped.y === snapY) {
          centerY = snapY;
          guides.push({ orientation: 'horizontal', position: snapY });
        }
      }

      node.position({ x: centerX, y: centerY });
      setAlignmentGuides(guides);

      // Keep state in step so the mirrored copy follows the drag
      updateElement(element.id, {
        x: toRatio(centerX - widthPx / 2, cardWidth),
        y: toRatio(centerY - heightPx / 2, cardHeight),
      });
    },
    [cardDimensions.heightPx, cardDimensions.widthPx, foldLineY, template?.elements, updateElement]
  );

  const handleDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>, element: PlaceCardElement) => {
      const node = e.target as Konva.Group;
      const widthPx = node.width();
      const heightPx = node.height();
      const center = clampElementCenter(
        node.x(),
        node.y(),
        widthPx,
        heightPx,
        node.rotation(),
        cardDimensions.widthPx,
        cardDimensions.heightPx
      );
      node.position(center);

      updateElement(element.id, {
        x: toRatio(center.x - widthPx / 2, cardDimensions.widthPx),
        y: toRatio(center.y - heightPx / 2, cardDimensions.heightPx),
      });

      setAlignmentGuides([]);
    },
    [cardDimensions.heightPx, cardDimensions.widthPx, updateElement]
  );

  const handleTransformStart = useCallback(() => {
    isTransformingRef.current = true;
  }, []);

  const handleTransformEnd = useCallback(
    (e: Konva.KonvaEventObject<Event>, element: PlaceCardElement) => {
      isTransformingRef.current = false;
      const node = e.target as Konva.Group;

      const widthPx = clamp(node.width() * node.scaleX(), MIN_ELEMENT_SIZE_PX, cardDimensions.widthPx);
      const heightPx = clamp(node.height() * node.scaleY(), MIN_ELEMENT_SIZE_PX, cardDimensions.heightPx);

      // Snap rotation to 5-degree increments
      const snappedRotation = Math.round(node.rotation() / 5) * 5;

      // Bake the scale into width/height and re-centre the offset so rotation stays about the centre
      node.scaleX(1);
      node.scaleY(1);
      node.width(widthPx);
      node.height(heightPx);
      node.offsetX(widthPx / 2);
      node.offsetY(heightPx / 2);
      node.rotation(snappedRotation);

      const center = clampElementCenter(
        node.x(),
        node.y(),
        widthPx,
        heightPx,
        snappedRotation,
        cardDimensions.widthPx,
        cardDimensions.heightPx
      );
      node.position(center);

      updateElement(element.id, {
        x: toRatio(center.x - widthPx / 2, cardDimensions.widthPx),
        y: toRatio(center.y - heightPx / 2, cardDimensions.heightPx),
        width: toRatio(widthPx, cardDimensions.widthPx),
        height: toRatio(heightPx, cardDimensions.heightPx),
        rotation: snappedRotation,
      });
    },
    [cardDimensions.heightPx, cardDimensions.widthPx, updateElement]
  );

  const previewModalPlaceholderMap = useMemo(() => {
    const seating = previewModalGuest ? seatingMap.get(previewModalGuest.id) : undefined;
    
    return buildPlaceholderMap(previewModalGuest ?? null, seating, event, preparedUnitInfo, unitTypeRanks);
  }, [previewModalGuest, seatingMap, event, preparedUnitInfo, unitTypeRanks]);

  const filteredTemplates = useMemo(() => {
    return existingTemplates.filter((tpl) => {
      if (isAdminPlatformMode) {
        return true;
      }
      if (selectedTemplateFilter === 'mine') {
        return !tpl.isAdminTemplate;
      }
      return true;
    });
  }, [existingTemplates, selectedTemplateFilter, isAdminPlatformMode]);

  const handleLoadTemplate = useCallback(
    async (tpl: PlaceCardTemplate) => {
      const current = templateRef.current;
      if (!isAdminPlatformMode && (current?.elements?.length ?? 0) > 0) {
        const result = await confirmAction({
          title: 'Replace current design?',
          text: `Loading "${tpl.name}" replaces this event's place card design. The current design will be overwritten.`,
          confirmButtonText: 'Replace',
          icon: 'warning',
        });
        if (!result.isConfirmed) {
          return;
        }
      }

      if (isAdminPlatformMode) {
        // Switching to another platform template: persist pending edits to the current one first
        try {
          await enqueueSave('auto');
        } catch (error) {
          console.error('Failed to save template before switching', error);
          showError('Could not save your changes to the current template');
          return;
        }
      }

      const cloned: PlaceCardTemplate = {
        ...tpl,
        elements: tpl.elements ? tpl.elements.map((el) => ({ ...el })) : [],
        fields: tpl.fields ? tpl.fields.map((field) => ({ ...field })) : [],
        createdAt: new Date(tpl.createdAt),
        updatedAt: new Date(),
      };

      if (isAdminPlatformMode) {
        const next: PlaceCardTemplate = {
          ...cloned,
          id: cloned.id || '',
          eventId: undefined,
          unitId: '',
          isDefault: false,
          isAdminTemplate: true,
        };
        // Loaded straight from Firestore, so it starts clean
        lastPersistedFingerprintRef.current = templateDesignFingerprint(next);
        templateRef.current = next;
        setTemplate(next);
      } else {
        setTemplate((prev) => {
          const sameEventCanvas = event && prev?.eventId === event.id;
          return {
            ...cloned,
            // Keep writing to this event's template document rather than the source template
            id: sameEventCanvas && prev?.id ? prev.id : '',
            unitId: unitId ?? cloned.unitId,
            eventId: event?.id ?? '',
            isDefault: true,
            isAdminTemplate: false,
          };
        });
      }
      setSelectedElementId(null);
    },
    [event, isAdminPlatformMode, enqueueSave, unitId]
  );

  const handlePrint = useCallback(async () => {
    if (!template || !event) {
      showError('Template not ready');
      return;
    }
    // Only attending guests: excludes apologies and cancelled/declined/waitlisted bookings
    const attendingRsvps = isAdminPlatformMode
      ? createAdminTemplatePreviewRsvps().filter(isAttendeeCountingRsvp)
      : cardRsvps.filter(isAttendeeCountingRsvp);

    if (attendingRsvps.length === 0) {
      showError('No attending guests found. Place cards can only be generated for guests who are attending.');
      return;
    }

    const tablesForPdf = isAdminPlatformMode ? [] : tables;

    setGeneratingPdf(true);
    try {
      const result = await generateCustomPlaceCards(attendingRsvps, event, tablesForPdf, template, preparedUnitInfo);
      const problem = describePlaceCardGenerationProblems(result);
      if (result.failedCards === result.totalCards) {
        showError(problem ?? 'Failed to generate place cards');
      } else if (problem) {
        showWarning(`PDF downloaded, but ${problem.charAt(0).toLowerCase()}${problem.slice(1)}`);
      } else if (isAdminPlatformMode) {
        showSuccess(
          `Sample PDF with ${result.totalCards} example guest${result.totalCards === 1 ? '' : 's'} (admin template is still shared with all units).`
        );
      } else {
        showSuccess(
          `Place cards generated for ${result.totalCards} attending guest${result.totalCards === 1 ? '' : 's'}`
        );
      }
    } catch (error) {
      console.error('Failed to generate place cards', error);
      showError('Failed to generate place cards');
    } finally {
      setGeneratingPdf(false);
    }
  }, [template, event, cardRsvps, tables, preparedUnitInfo, isAdminPlatformMode]);

  const elements = useMemo(() => template?.elements ?? [], [template?.elements]);

  const selectedElement = elements.find((el) => el.id === selectedElementId) ?? null;
  const lockSelectedAspectRatio =
    selectedElement?.type === 'image' && selectedElement.preserveAspectRatio !== false;

  const resetImageAspectRatio = useCallback(() => {
    if (!selectedElement || selectedElement.type !== 'image') {
      return;
    }

    const loadedImage = resolveElementImage(selectedElement);
    const ratio = getPlaceCardElementNativeAspectRatio(selectedElement, loadedImage);
    if (!ratio) {
      return;
    }

    const centerX = (selectedElement.x ?? 0) + (selectedElement.width ?? 0.2) / 2;
    const centerY = (selectedElement.y ?? 0) + (selectedElement.height ?? 0.1) / 2;
    const newWidth = selectedElement.width ?? 0.2;
    const newHeight = Math.max(0.02, newWidth / ratio);

    updateElement(selectedElement.id, {
      width: newWidth,
      height: newHeight,
      x: clamp(centerX - newWidth / 2, 0, 1 - newWidth),
      y: clamp(centerY - newHeight / 2, 0, 1 - newHeight),
      nativeAspectRatio: ratio,
    });
  }, [resolveElementImage, selectedElement, updateElement]);

  const createElementNode = (
    element: PlaceCardElement,
    placeholderMap: Record<string, string>,
    options: { interactive: boolean; isMirrored?: boolean }
  ) => {
    const { interactive, isMirrored = false } = options;
    const x = (element.x ?? 0) * cardDimensions.widthPx;
    const y = (element.y ?? 0) * cardDimensions.heightPx;
    const width = (element.width ?? 0.2) * cardDimensions.widthPx;
    const height = (element.height ?? 0.1) * cardDimensions.heightPx;

    const rotation = element.rotation ?? 0;
    const opacity = element.opacity ?? 1;
    const idSuffix = isMirrored ? '-mirror' : '';

    const textContent =
      element.type === 'text'
        ? (interactive ? (element.content ?? '') : applyPlaceholders(element.content ?? '', placeholderMap))
        : '';

    // Only render a background when the element defines one; never draw selection borders in preview
    const backgroundFill = element.backgroundColor;

    // Always rotate around element center for consistent behavior
    const groupOffsetX = width / 2;
    const groupOffsetY = height / 2;
    // x and y represent top-left corner, but Konva x/y with offset represent the offset point
    // So we need: node.x = storedX + offsetX to position top-left at storedX
    const groupX = x + groupOffsetX;
    const groupY = y + groupOffsetY;

    // Konva's fontStyle is the CSS font prefix, so it can carry the numeric weight the PDF uses too
    const konvaFontStyle = `${element.fontStyle === 'italic' ? 'italic ' : ''}${cssFontWeight(element.fontWeight)}`;

    return (
      <Group
        key={`${element.id}${idSuffix}`}
        id={`element-${element.id}${idSuffix}`}
        x={groupX}
        y={groupY}
        width={width}
        height={height}
        offsetX={groupOffsetX}
        offsetY={groupOffsetY}
        draggable={interactive && !isMirrored}
        rotation={rotation}
        opacity={opacity}
        onDragMove={interactive ? (e) => handleDragMove(e, element) : undefined}
        onDragEnd={interactive ? (e) => handleDragEnd(e, element) : undefined}
        onTransformStart={interactive ? handleTransformStart : undefined}
        onTransform={interactive ? () => {
          // Keep element selected during transform
          if (selectedElementId !== element.id) {
            setSelectedElementId(element.id);
          }
        } : undefined}
        onTransformEnd={interactive ? (e) => handleTransformEnd(e, element) : undefined}
        onClick={
          interactive
            ? (e) => {
                setSelectedElementId(element.id);
                e.cancelBubble = true;
              }
            : undefined
        }
        onTap={
          interactive
            ? (e) => {
                setSelectedElementId(element.id);
                e.cancelBubble = true;
              }
            : undefined
        }
        listening={interactive && !isMirrored}
      >
        {(backgroundFill || (element.strokeColor && element.strokeWidth)) && (
          <Rect
            width={width}
            height={height}
            cornerRadius={element.borderRadius ?? 0}
            fill={backgroundFill}
            stroke={element.strokeColor && element.strokeWidth ? element.strokeColor : undefined}
            strokeWidth={element.strokeColor && element.strokeWidth ? element.strokeWidth : 0}
          />
        )}
        {element.type === 'text' && (
          <Text
            // Remount when web fonts finish loading so Konva re-measures the text
            key={`text-${fontsVersion}`}
            text={textContent || element.content || ''}
            width={width}
            height={height}
            // Matches the PDF renderer's 4px padding, line height and letter spacing
            padding={4}
            lineHeight={element.lineHeight ?? 1.2}
            letterSpacing={element.letterSpacing ?? 0}
            fontSize={element.fontSize ?? 24}
            fontFamily={element.fontFamily ?? 'Georgia, serif'}
            fontStyle={konvaFontStyle}
            fontVariant="normal"
            textDecoration={element.textDecoration ?? 'none'}
            align={element.textAlign ?? 'center'}
            fill={element.color ?? '#111827'}
            verticalAlign="middle"
            listening={interactive && !isMirrored}
          />
        )}
        {element.type === 'image' && (
          <KonvaImageElement
            width={width}
            height={height}
            image={resolveElementImage(element)}
            mirrored={isMirrored}
          />
        )}
      </Group>
    );
  };

  const createMirroredElementNode = (
    element: PlaceCardElement,
    placeholderMap: Record<string, string>
  ) => {
    if (!element.mirrored) {
      return null;
    }

    const height = (element.height ?? 0.1) * cardDimensions.heightPx;
    const originalX = (element.x ?? 0) * cardDimensions.widthPx;
    const originalY = (element.y ?? 0) * cardDimensions.heightPx;
    const mirrorRotation = element.mirrorRotation ?? 180;

    const originalCenterY = originalY + height / 2;
    const mirroredCenterY = 2 * foldLineY - originalCenterY;
    const mirroredY = clamp(mirroredCenterY - height / 2, 0, cardDimensions.heightPx - height);

    const mirroredElement: PlaceCardElement = {
      ...element,
      id: `${element.id}-mirror`,
      x: toRatio(originalX, cardDimensions.widthPx),
      y: toRatio(mirroredY, cardDimensions.heightPx),
      rotation: (element.rotation ?? 0) + mirrorRotation,
    };

    return createElementNode(mirroredElement, placeholderMap, { interactive: false, isMirrored: true });
  };

  const renderCardElements = (placeholderMap: Record<string, string>, interactive: boolean) => {
    const nodes: JSX.Element[] = [];
    elements.forEach((element) => {
      nodes.push(createElementNode(element, placeholderMap, { interactive, isMirrored: false }));
      if (element.mirrored) {
        const mirroredNode = createMirroredElementNode(element, placeholderMap);
        if (mirroredNode) {
          nodes.push(mirroredNode);
        }
      }
    });
    return nodes;
  };

  const handleUpdateSelectedElement = useCallback(
    (updates: Partial<PlaceCardElement>) => {
      if (!selectedElement) {
        return;
      }
      let nextUpdates = { ...updates };
      // Text blocks are named after their content until the user gives them a name of their own
      if (updates.content !== undefined && selectedElement.type === 'text') {
        const currentAutoName = inferTextElementName(selectedElement.content);
        const isAutoNamed =
          !selectedElement.name ||
          selectedElement.name === currentAutoName ||
          selectedElement.name === selectedElement.content;
        if (isAutoNamed) {
          nextUpdates = { ...nextUpdates, name: inferTextElementName(updates.content) };
        }
      }
      updateElement(selectedElement.id, nextUpdates);
    },
    [selectedElement, updateElement]
  );

  const handleInsertPlaceholder = useCallback(
    (token: PlaceCardPlaceholder) => {
      if (!selectedElement) {
        return;
      }

      const textarea = contentTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart ?? textarea.value.length;
        const end = textarea.selectionEnd ?? textarea.value.length;
        const current = textarea.value ?? '';
        handleUpdateSelectedElement({ content: `${current.slice(0, start)}${token}${current.slice(end)}` });

        requestAnimationFrame(() => {
          textarea.focus();
          const caret = start + token.length;
          textarea.selectionStart = caret;
          textarea.selectionEnd = caret;
        });
      } else {
        handleUpdateSelectedElement({ content: `${selectedElement.content ?? ''}${token}` });
      }

      setPendingPlaceholder('');
    },
    [handleUpdateSelectedElement, selectedElement]
  );

  const confirmRemoveElement = useCallback(
    async (element: PlaceCardElement) => {
      const result = await confirmAction({
        title: 'Delete Element',
        text: `Are you sure you want to delete "${element.name ?? 'this element'}"?`,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#dc2626',
      });
      if (result.isConfirmed) {
        removeElement(element.id);
      }
    },
    [removeElement]
  );

  // Keyboard UX: arrow keys nudge, delete removes, Esc clears selection, Ctrl/Cmd+S saves, Ctrl/Cmd+P previews
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      if (isMod && key === 's') {
        e.preventDefault();
        void handleManualSave();
        return;
      }

      if (isMod && key === 'p') {
        // Only take over the browser's print shortcut when there is a preview to show
        if (previewGuests.length > 0 && !previewModalOpen) {
          e.preventDefault();
          openPreviewModal();
        }
        return;
      }

      // Canvas shortcuts only apply when nothing else has focus (not inputs, buttons, menus or dialogs)
      const active = document.activeElement as HTMLElement | null;
      const focusIsFree =
        !active ||
        active === document.body ||
        (canvasContainer !== null && canvasContainer.contains(active));
      if (!focusIsFree || previewModalOpen || Swal.isVisible() || !selectedElementId) {
        return;
      }

      const sel = elements.find((el) => el.id === selectedElementId);
      if (!sel) {
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        void confirmRemoveElement(sel);
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedElementId(null);
        return;
      }

      const stepPx = e.shiftKey ? 10 : 5;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowLeft') dx = -stepPx;
      if (e.key === 'ArrowRight') dx = stepPx;
      if (e.key === 'ArrowUp') dy = -stepPx;
      if (e.key === 'ArrowDown') dy = stepPx;

      if (dx !== 0 || dy !== 0) {
        e.preventDefault();
        const widthPx = (sel.width ?? 0.2) * cardDimensions.widthPx;
        const heightPx = (sel.height ?? 0.1) * cardDimensions.heightPx;
        const center = clampElementCenter(
          (sel.x ?? 0) * cardDimensions.widthPx + widthPx / 2 + dx,
          (sel.y ?? 0) * cardDimensions.heightPx + heightPx / 2 + dy,
          widthPx,
          heightPx,
          sel.rotation ?? 0,
          cardDimensions.widthPx,
          cardDimensions.heightPx
        );
        updateElement(sel.id, {
          x: toRatio(center.x - widthPx / 2, cardDimensions.widthPx),
          y: toRatio(center.y - heightPx / 2, cardDimensions.heightPx),
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    selectedElementId,
    elements,
    cardDimensions.widthPx,
    cardDimensions.heightPx,
    handleManualSave,
    openPreviewModal,
    previewGuests.length,
    previewModalOpen,
    canvasContainer,
    confirmRemoveElement,
    updateElement,
  ]);

  if (loadError) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-base font-medium">Place card designer unavailable</p>
          <p className="max-w-md text-sm text-muted-foreground">{loadError}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={() => window.location.reload()}>Try again</Button>
          </div>
        </main>
      </div>
    );
  }

  if (loading || !template || !event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 flex-col items-center justify-center p-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">Loading place card designer...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <DashboardContentContainer className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto py-6 sm:py-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (isAdminPlatformMode) {
                    // Only fall back to the local admin app in development; otherwise just go back
                    const adminBase =
                      import.meta.env.VITE_ADMIN_URL?.replace(/\/$/, '') ||
                      (import.meta.env.DEV ? 'http://localhost:3004' : '');
                    if (adminBase) {
                      window.location.href = `${adminBase}/dashboard`;
                      return;
                    }
                  }
                  navigate(-1);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Place Card Designer</h1>
                <p className="text-sm text-muted-foreground">
                  {`${event.name} · ${event.location}`}
                </p>
                {isAdminPlatformMode && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Saves as an admin template for every unit. PDF and preview use example guests and unit text.
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {autoSaveStatus === 'saving' && 'Saving…'}
                  {autoSaveStatus === 'saved' && 'All changes saved'}
                  {autoSaveStatus === 'error' && 'Autosave failed. Use Save to retry.'}
                  {autoSaveStatus === 'idle' &&
                    (isAdminPlatformMode
                      ? 'Design saves automatically. These templates appear for all units to load.'
                      : 'Design saves automatically when you make changes.')}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleSaveAsTemplate} disabled={savingTemplate}>
                <Copy className="mr-2 h-4 w-4" />
                Save As
              </Button>
              <Button variant="outline" onClick={handleManualSave} disabled={savingTemplate}>
                <Save className="mr-2 h-4 w-4" />
                {savingTemplate ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" onClick={handlePrint} disabled={generatingPdf}>
                <Printer className="mr-2 h-4 w-4" />
                {generatingPdf ? 'Generating, please wait…' : 'Generate PDF'}
              </Button>
              <Button variant="outline" onClick={openPreviewModal} disabled={previewGuests.length === 0}>
                <Eye className="mr-2 h-4 w-4" />
                Preview Cards
              </Button>
            </div>
          </div>

          <div className="grid flex-1 gap-6 lg:grid-cols-[320px_1fr_320px]">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Template Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Template Name</label>
                    <Input
                      className="mt-1"
                      value={template.name}
                      onChange={(e) => updateTemplate({ name: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Paper Size</label>
                      <Select
                        value={template.paperSize}
                        onValueChange={(value) => {
                          const next = value as PaperSize;
                          if (next === 'A5') {
                            updateCardLayout({ paperSize: next, cardSize: '5.8x8.3' as CardSize, cardsPerPage: 1 });
                          } else {
                            updateCardLayout({ paperSize: next });
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
                          <SelectItem value="A5">A5 (148 × 210 mm)</SelectItem>
                          <SelectItem value="Letter">Letter (8.5 × 11 in)</SelectItem>
                          <SelectItem value="Legal">Legal (8.5 × 14 in)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Cards Per Page</label>
                      <DraftNumberInput
                        value={template.cardsPerPage}
                        min={1}
                        max={cardGrid.maxCardsPerPage}
                        step="1"
                        onCommit={(value) => updateCardLayout({ cardsPerPage: Math.round(value) })}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Up to {cardGrid.maxCardsPerPage} fit on {template.paperSize}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Card Size</label>
                    <Select
                      value={template.cardSize}
                      onValueChange={(value) => updateCardLayout({ cardSize: value as CardSize })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3x5">3 × 5 in</SelectItem>
                        <SelectItem value="4x6">4 × 6 in</SelectItem>
                        <SelectItem value="5x7">5 × 7 in</SelectItem>
                        <SelectItem value="5.8x8.3">A5 (5.8 × 8.3 in)</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {template.cardSize === 'custom' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium">Width (in)</label>
                        <DraftNumberInput
                          value={cardDimensions.widthInches}
                          min={MIN_CUSTOM_CARD_INCHES}
                          max={PAPER_SIZE_INCHES[template.paperSize].width}
                          step="0.1"
                          onCommit={(value) => updateCardLayout({ customCardWidth: value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Height (in)</label>
                        <DraftNumberInput
                          value={cardDimensions.heightInches}
                          min={MIN_CUSTOM_CARD_INCHES}
                          max={PAPER_SIZE_INCHES[template.paperSize].height}
                          step="0.1"
                          onCommit={(value) => updateCardLayout({ customCardHeight: value })}
                        />
                      </div>
                    </div>
                  )}
                  {!cardGrid.cardFitsPaper && (
                    <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                      This card is larger than {template.paperSize} paper and will be cropped when printed.
                      Choose a smaller card or larger paper.
                    </p>
                  )}
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <div>
                      <p className="text-sm font-medium">Show Fold Line</p>
                      <p className="text-xs text-muted-foreground">
                        Display a guide for where the card folds
                      </p>
                    </div>
                    <Switch
                      checked={template.layout?.showFoldLine ?? true}
                      onCheckedChange={(checked) =>
                        updateTemplate({
                          layout: {
                            ...template.layout,
                            showFoldLine: checked,
                          },
                        })
                      }
                    />
                  </div>
                  {/* Fold position is fixed to centre; no input required */}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Frame</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <div>
                      <p className="text-sm font-medium">Show Frame</p>
                      <p className="text-xs text-muted-foreground">
                        Adds a border around each panel, mirrored on both sides of the fold
                      </p>
                    </div>
                    <Switch
                      checked={template.layout?.frame?.enabled ?? false}
                      onCheckedChange={(checked) => updateFrame({ enabled: checked })}
                    />
                  </div>
                  {template.layout?.frame?.enabled && (
                    <>
                      <div>
                        <label className="text-sm font-medium">Frame Style</label>
                        <Select
                          value={template.layout?.frame?.style ?? 'solid'}
                          onValueChange={(value) => updateFrame({ style: value as PlaceCardFrameStyle })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solid">Solid</SelectItem>
                            <SelectItem value="rounded">Rounded Corners</SelectItem>
                            <SelectItem value="double">Double Line</SelectItem>
                            <SelectItem value="dashed">Dashed</SelectItem>
                            <SelectItem value="dotted">Dotted</SelectItem>
                            <SelectItem value="modern">Modern (open corners)</SelectItem>
                            <SelectItem value="elegant">Elegant (corner accents)</SelectItem>
                            <SelectItem value="rustic">Rustic (rope edge)</SelectItem>
                            <SelectItem value="royal">Royal (wavy &amp; scrolled)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium">Thickness (px)</label>
                          <Input
                            className="mt-1"
                            type="number"
                            min="1"
                            step="1"
                            value={template.layout?.frame?.thickness ?? DEFAULT_PLACE_CARD_FRAME.thickness}
                            onChange={(e) => updateFrame({ thickness: Math.max(1, Number(e.target.value) || 1) })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Colour</label>
                          <Input
                            className="mt-1 h-10"
                            type="color"
                            value={template.layout?.frame?.color ?? DEFAULT_PLACE_CARD_FRAME.color}
                            onChange={(e) => updateFrame({ color: e.target.value })}
                          />
                        </div>
                      </div>
                      {template.layout?.frame?.style === 'rounded' && (
                        <div>
                          <label className="text-sm font-medium">Corner Radius (px)</label>
                          <Input
                            className="mt-1"
                            type="number"
                            min="0"
                            step="1"
                            value={template.layout?.frame?.cornerRadius ?? DEFAULT_PLACE_CARD_FRAME.cornerRadius}
                            onChange={(e) => updateFrame({ cornerRadius: Math.max(0, Number(e.target.value) || 0) })}
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium">Padding from Edge (px)</label>
                        <Input
                          className="mt-1"
                          type="number"
                          min="0"
                          step="1"
                          value={template.layout?.frame?.padding ?? DEFAULT_PLACE_CARD_FRAME.padding}
                          onChange={(e) => updateFrame({ padding: Math.max(0, Number(e.target.value) || 0) })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium">Offset X (px)</label>
                          <Input
                            className="mt-1"
                            type="number"
                            step="1"
                            value={template.layout?.frame?.offsetX ?? DEFAULT_PLACE_CARD_FRAME.offsetX}
                            onChange={(e) => updateFrame({ offsetX: Number(e.target.value) || 0 })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Offset Y (px)</label>
                          <Input
                            className="mt-1"
                            type="number"
                            step="1"
                            value={template.layout?.frame?.offsetY ?? DEFAULT_PLACE_CARD_FRAME.offsetY}
                            onChange={(e) => updateFrame({ offsetY: Number(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        The frame is drawn around the top panel and automatically mirrored onto the bottom panel across the fold line.
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Element Library</CardTitle>
                  <Badge variant="outline">{elements.length} elements</Badge>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" className="w-full justify-start" onClick={addTextElement}>
                    <Type className="mr-2 h-4 w-4" />
                    Add Text Block
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ImageIcon className="mr-2 h-4 w-4" />
                    )}
                    {uploadingImage ? 'Uploading…' : 'Upload Image'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void handleImageUpload(e)}
                  />
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={addUnitLogoElement}
                    disabled={!unitLogoUrl}
                  >
                    <ImageIcon className="mr-2 h-4 w-4" />
                    Add Unit Logo
                  </Button>
                  {!unitLogoUrl && (
                    <p className="text-xs text-muted-foreground">
                      {isAdminPlatformMode
                        ? 'Logo elements use the hosting unit logo when members design or print place cards for their events.'
                        : 'Add a logo in your unit settings to enable logo elements.'}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Drop text blocks, then insert placeholders via the Selected Element editor.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Saved Templates</CardTitle>
                  {isAdminPlatformMode && (
                    <p className="mt-1 text-xs font-normal text-muted-foreground">
                      Admin templates are shared: every unit can load them from this list when designing place cards.
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isAdminPlatformMode && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={selectedTemplateFilter === 'all' ? 'default' : 'outline'}
                        onClick={() => setSelectedTemplateFilter('all')}
                      >
                        All
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedTemplateFilter === 'mine' ? 'default' : 'outline'}
                        onClick={() => setSelectedTemplateFilter('mine')}
                      >
                        My Templates
                      </Button>
                    </div>
                  )}
                  <div className="h-64 overflow-y-auto overflow-x-hidden pr-2">
                    <div className="space-y-3">
                      {loadingTemplates && (
                        <p className="text-sm text-muted-foreground">Loading templates…</p>
                      )}
                      {!loadingTemplates && filteredTemplates.length === 0 && (
                        <p className="text-sm text-muted-foreground">No templates found.</p>
                      )}
                      {filteredTemplates.map((tpl) => (
                        <div
                          key={tpl.id}
                          className="rounded-md border border-border p-3"
                        >
                          <div className="flex flex-col gap-2">
                            <div className="min-w-0">
                              <p className="break-words text-sm font-medium">
                                {tpl.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Updated {tpl.updatedAt.toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {tpl.isAdminTemplate && !isAdminPlatformMode && <Badge variant="secondary">Platform</Badge>}
                              <Button size="sm" variant="outline" onClick={() => void handleLoadTemplate(tpl)}>
                                Load
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDuplicateTemplate(tpl)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                              {(isAdminPlatformMode || !tpl.isAdminTemplate) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteTemplate(tpl.id, tpl.isAdminTemplate)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex h-full min-w-0 flex-col space-y-4">
              <Card className="flex-1 shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    Canvas · {cardDimensions.widthInches.toFixed(2)} × {cardDimensions.heightInches.toFixed(2)} in
                  </CardTitle>
                </CardHeader>
                <div className="flex items-center gap-2 border-b px-6 pb-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={resetImageAspectRatio}
                    disabled={!selectedElement || selectedElement.type !== 'image'}
                    title="Reset Aspect Ratio"
                  >
                    <Ratio className="h-4 w-4" />
                  </Button>
                </div>
                <CardContent>
                  <div ref={setCanvasContainer} className="flex h-[70vh] min-h-[420px] min-w-0 justify-center overflow-hidden rounded-2xl bg-muted p-2 md:h-[75vh]">
                      <Stage
                        width={cardDimensions.widthPx * canvasScale}
                        height={cardDimensions.heightPx * canvasScale}
                        scaleX={canvasScale}
                        scaleY={canvasScale}
                        ref={stageRef}
                        onClick={handleStageClick}
                        onTap={handleStageClick}
                      >
                        <Layer>
                          <Rect
                            width={cardDimensions.widthPx}
                            height={cardDimensions.heightPx}
                            fill="#ffffff"
                          />
                          {template.layout?.showFoldLine !== false && (
                            <Line
                              points={[0, foldLineY, cardDimensions.widthPx, foldLineY]}
                              stroke="#94a3b8"
                              dash={[8, 6]}
                              strokeWidth={1}
                            />
                          )}
                          {renderFrame(false)}
                          {elements.length === 0 && (
                            <Text
                              text="Add elements from the library to begin"
                              width={cardDimensions.widthPx}
                              height={cardDimensions.heightPx}
                              align="center"
                              verticalAlign="middle"
                              fontSize={18}
                              fill="#64748b"
                            />
                          )}
                          {renderCardElements({}, true)}
                          {alignmentGuides.map((guide, index) => (
                            <Line
                              key={`${guide.orientation}-${guide.position}-${index}`}
                              points={
                                guide.orientation === 'horizontal'
                                  ? [0, guide.position, cardDimensions.widthPx, guide.position]
                                  : [guide.position, 0, guide.position, cardDimensions.heightPx]
                              }
                              stroke="rgba(59,130,246,0.6)"
                              dash={[6, 4]}
                              strokeWidth={1}
                            />
                          ))}
                          <Transformer
                            ref={transformerRef}
                            rotateEnabled
                            keepRatio={lockSelectedAspectRatio}
                            enabledAnchors={
                              lockSelectedAspectRatio
                                ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
                                : [
                                    'top-left',
                                    'top-right',
                                    'bottom-left',
                                    'bottom-right',
                                    'top-center',
                                    'bottom-center',
                                    'middle-left',
                                    'middle-right',
                                  ]
                            }
                            boundBoxFunc={(oldBox, newBox) => {
                              // Transformer boxes are in screen pixels, so scale the card-pixel minimum
                              const minSize = MIN_ELEMENT_SIZE_PX * canvasScale;
                              if (Math.abs(newBox.width) < minSize || Math.abs(newBox.height) < minSize) {
                                return oldBox;
                              }
                              return newBox;
                            }}
                          />
                        </Layer>
                      </Stage>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              {/* Preview Guest box removed as per user request */}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    Layers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {elements.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No elements yet. Add elements from the library.</p>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event: DragEndEvent) => {
                        const { active, over } = event;
                        if (over && active.id !== over.id) {
                          reorderElements(active.id as string, over.id as string);
                        }
                      }}
                    >
                      <SortableContext
                        items={elements.map((el) => el.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {/* Render in reverse order so top layer (last in array) appears first in UI */}
                          {[...elements].reverse().map((element) => (
                            <SortableLayerItem
                              key={element.id}
                              element={element}
                              isSelected={selectedElementId === element.id}
                              onSelect={() => setSelectedElementId(element.id)}
                              onDuplicate={() => duplicateElement(element.id)}
                              onDelete={() => void confirmRemoveElement(element)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Selected Element</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!selectedElement && (
                    <p className="text-sm text-muted-foreground">
                      Select an element on the canvas to edit its styling and behaviour.
                    </p>
                  )}
                  {selectedElement && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">Name</label>
                        <Input
                          className="mt-1"
                          value={selectedElement.name ?? ''}
                          placeholder={selectedElement.type === 'text' ? 'Text Element' : 'Image'}
                          onChange={(e) => handleUpdateSelectedElement({ name: e.target.value })}
                        />
                      </div>
                      {selectedElement.type === 'text' && (
                        <>
                      <div>
                        <label className="text-sm font-medium">Content</label>
                        <Textarea
                          ref={contentTextareaRef}
                          className="mt-1"
                          rows={4}
                          value={selectedElement.content ?? ''}
                          onChange={(e) => handleUpdateSelectedElement({ content: e.target.value })}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          Use placeholders to merge event data and guest details.
                        </p>
                        <div className="flex items-center gap-2 pt-2">
                          <Select
                            value={pendingPlaceholder}
                            onValueChange={(value) => setPendingPlaceholder(value as PlaceCardPlaceholder)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Insert placeholder…" />
                            </SelectTrigger>
                            <SelectContent>
                              {PLACEHOLDER_OPTIONS.map((option) => (
                                <SelectItem key={option.token} value={option.token}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="whitespace-nowrap"
                            onClick={() => pendingPlaceholder && handleInsertPlaceholder(pendingPlaceholder)}
                            disabled={!pendingPlaceholder}
                          >
                            Insert
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium">Font Size</label>
                          <Input
                            className="mt-1"
                            type="number"
                            min={8}
                            max={96}
                            value={selectedElement.fontSize ?? 24}
                            onChange={(e) => handleUpdateSelectedElement({ fontSize: Number(e.target.value) || 12 })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Line Height</label>
                          <Input
                            className="mt-1"
                            type="number"
                            step="0.05"
                            min="0.8"
                            max="2"
                            value={selectedElement.lineHeight ?? 1.2}
                            onChange={(e) => handleUpdateSelectedElement({ lineHeight: Number(e.target.value) || 1.2 })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Letter Spacing (px)</label>
                        <Input
                          className="mt-1"
                          type="number"
                          step="0.5"
                          min="-5"
                          max="20"
                          value={selectedElement.letterSpacing ?? 0}
                          onChange={(e) => handleUpdateSelectedElement({ letterSpacing: Number(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Font Family</label>
                        <Select
                          value={selectedElement.fontFamily ?? 'Georgia, serif'}
                          onValueChange={(value) => handleUpdateSelectedElement({ fontFamily: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PLACE_CARD_FONT_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                            {selectedElement.fontFamily &&
                              !PLACE_CARD_FONT_OPTIONS.some((option) => option.value === selectedElement.fontFamily) && (
                                <SelectItem value={selectedElement.fontFamily}>
                                  {selectedElement.fontFamily.split(',')[0]}
                                </SelectItem>
                              )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium">Style</label>
                          <div className="mt-1 flex gap-2">
                            <Button
                              type="button"
                              variant={(selectedElement.fontWeight ?? 'normal') === 'bold' ? 'default' : 'outline'}
                              size="icon"
                              onClick={() =>
                                handleUpdateSelectedElement({
                                  fontWeight: (selectedElement.fontWeight ?? 'normal') === 'bold' ? 'normal' : 'bold',
                                })
                              }
                              title="Bold"
                            >
                              <BoldIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={(selectedElement.fontStyle ?? 'normal') === 'italic' ? 'default' : 'outline'}
                              size="icon"
                              onClick={() =>
                                handleUpdateSelectedElement({
                                  fontStyle: (selectedElement.fontStyle ?? 'normal') === 'italic' ? 'normal' : 'italic',
                                })
                              }
                              title="Italic"
                            >
                              <ItalicIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={(selectedElement.textDecoration ?? 'none') === 'underline' ? 'default' : 'outline'}
                              size="icon"
                              onClick={() =>
                                handleUpdateSelectedElement({
                                  textDecoration:
                                    (selectedElement.textDecoration ?? 'none') === 'underline' ? 'none' : 'underline',
                                })
                              }
                              title="Underline"
                            >
                              <UnderlineIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Text Colour</label>
                          <Input
                            className="mt-1 h-10"
                            type="color"
                            value={selectedElement.color ?? '#1f2937'}
                            onChange={(e) => handleUpdateSelectedElement({ color: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Alignment</label>
                        <div className="mt-1 flex gap-2">
                          <Button
                            type="button"
                            variant={selectedElement.textAlign === 'left' ? 'default' : 'outline'}
                            size="icon"
                            onClick={() => handleUpdateSelectedElement({ textAlign: 'left' })}
                          >
                            <AlignLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant={selectedElement.textAlign === 'center' ? 'default' : 'outline'}
                            size="icon"
                            onClick={() => handleUpdateSelectedElement({ textAlign: 'center' })}
                          >
                            <AlignCenter className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant={selectedElement.textAlign === 'right' ? 'default' : 'outline'}
                            size="icon"
                            onClick={() => handleUpdateSelectedElement({ textAlign: 'right' })}
                          >
                            <AlignRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                        </>
                      )}
                      {selectedElement.type === 'image' && (
                        <div className="flex items-center justify-between rounded-md border p-2">
                          <div>
                            <p className="text-sm font-medium">Lock Aspect Ratio</p>
                            <p className="text-xs text-muted-foreground">Resize without stretching the image</p>
                          </div>
                          <Switch
                            checked={selectedElement.preserveAspectRatio !== false}
                            onCheckedChange={(checked) => handleUpdateSelectedElement({ preserveAspectRatio: checked })}
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium">Opacity (%)</label>
                          <Input
                            className="mt-1"
                            type="number"
                            min={0}
                            max={100}
                            step={5}
                            value={Math.round((selectedElement.opacity ?? 1) * 100)}
                            onChange={(e) =>
                              handleUpdateSelectedElement({ opacity: clamp(Number(e.target.value) || 0, 0, 100) / 100 })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Rotation (°)</label>
                          <Input
                            className="mt-1"
                            type="number"
                            min={-180}
                            max={180}
                            step={5}
                            value={selectedElement.rotation ?? 0}
                            onChange={(e) => {
                              const rotation = clamp(Number(e.target.value) || 0, -180, 180);
                              const widthPx = (selectedElement.width ?? 0.2) * cardDimensions.widthPx;
                              const heightPx = (selectedElement.height ?? 0.1) * cardDimensions.heightPx;
                              const center = clampElementCenter(
                                (selectedElement.x ?? 0) * cardDimensions.widthPx + widthPx / 2,
                                (selectedElement.y ?? 0) * cardDimensions.heightPx + heightPx / 2,
                                widthPx,
                                heightPx,
                                rotation,
                                cardDimensions.widthPx,
                                cardDimensions.heightPx
                              );
                              handleUpdateSelectedElement({
                                rotation,
                                x: toRatio(center.x - widthPx / 2, cardDimensions.widthPx),
                                y: toRatio(center.y - heightPx / 2, cardDimensions.heightPx),
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Background</label>
                        <div className="mt-1 flex items-center gap-2">
                          <Input
                            className="h-10 w-20"
                            type="color"
                            value={selectedElement.backgroundColor ?? '#ffffff'}
                            onChange={(e) => handleUpdateSelectedElement({ backgroundColor: e.target.value })}
                          />
                          {selectedElement.backgroundColor ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateSelectedElement({ backgroundColor: undefined })}
                            >
                              Clear
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">None</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Position</label>
                        <div className="mt-1 flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              const elWidth = selectedElement.width ?? 0.2;
                              handleUpdateSelectedElement({ x: clamp((1 - elWidth) / 2, 0, 1 - elWidth) });
                            }}
                            title="Center Horizontally"
                          >
                            <AlignHorizontalJustifyCenter className="h-4 w-4 mr-1.5" />
                            Center H
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              const elHeight = selectedElement.height ?? 0.1;
                              handleUpdateSelectedElement({ y: clamp((1 - elHeight) / 2, 0, 1 - elHeight) });
                            }}
                            title="Center Vertically"
                          >
                            <AlignVerticalJustifyCenter className="h-4 w-4 mr-1.5" />
                            Center V
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-2">
                        <div>
                          <p className="text-sm font-medium">Mirror Across Fold</p>
                          <p className="text-xs text-muted-foreground">
                            Duplicate on the opposite side with rotation
                          </p>
                        </div>
                        <Switch
                          checked={selectedElement.mirrored ?? false}
                          onCheckedChange={(checked) => handleUpdateSelectedElement({ mirrored: checked })}
                        />
                      </div>
                    </div>
                  )}
                  {selectedElement && (
                    <div className="flex justify-end">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => selectedElement && void confirmRemoveElement(selectedElement)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove Element
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </DashboardContentContainer>
      </main>
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Place Card Preview</DialogTitle>
            <DialogDescription>
              {previewGuests.length === 0
                ? isAdminPlatformMode
                  ? 'No preview guests available.'
                  : 'Add RSVPs to preview personalised place cards.'
                : isAdminPlatformMode
                  ? 'Example guests and unit text show how placeholders will look. Units use their own data when printing.'
                  : 'Review each guest card before exporting.'}
            </DialogDescription>
          </DialogHeader>
          {previewGuests.length === 0 ? (
            <div className="rounded-md bg-slate-50 p-6 text-sm text-muted-foreground">
              No RSVPs are available yet. Configure your template now so cards are ready once guests respond.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium">{previewModalGuest?.name ?? 'Guest'}</p>
                  <p className="text-xs text-muted-foreground">
                    {previewModalPlaceholderMap['{guest_table}'] || 'Unassigned'} · Seat{' '}
                    {previewModalPlaceholderMap['{guest_seat}'] || '–'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Guest {previewCursor + 1} of {previewGuests.length}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handlePreviewModalPrevious}
                    title="Previous guest"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handlePreviewModalNext} title="Next guest">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    className="bg-emerald-500 text-white hover:bg-emerald-400"
                    onClick={handlePrint}
                    disabled={generatingPdf}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    {generatingPdf ? 'Generating, please wait…' : 'Export PDF'}
                  </Button>
                </div>
              </div>
              <div className="flex justify-center overflow-auto rounded-2xl bg-muted p-6">
                <div className="relative rounded-xl border border-border bg-white p-4 shadow">
                  <Stage width={cardDimensions.widthPx} height={cardDimensions.heightPx}>
                    <Layer>
                      <Rect
                        width={cardDimensions.widthPx}
                        height={cardDimensions.heightPx}
                        cornerRadius={12}
                        fill="#ffffff"
                        stroke="#e2e8f0"
                        strokeWidth={2}
                        shadowBlur={6}
                        shadowColor="rgba(15, 23, 42, 0.2)"
                      />
                      {template.layout?.showFoldLine !== false && (
                        <Line
                          points={[0, foldLineY, cardDimensions.widthPx, foldLineY]}
                          stroke="#94a3b8"
                          dash={[8, 6]}
                          strokeWidth={1}
                        />
                      )}
                      {renderFrame(false)}
                      {renderCardElements(previewModalPlaceholderMap, false)}
                    </Layer>
                  </Stage>
                </div>
              </div>
              <p className="text-center text-xs text-muted-foreground">
                Use the arrows to review each guest&rsquo;s card before printing.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function prepareTemplateForSave(template: PlaceCardTemplate) {
  const sanitizeElement = (element: PlaceCardElement) => {
    const cleanEntries = Object.entries(element).filter(
      ([, value]) => value !== undefined && value !== null
    );
    return Object.fromEntries(cleanEntries);
  };

  // The document id lives in the path, not the data
  const { id: _id, ...withoutId } = template;
  const cleanTemplate = {
    ...withoutId,
    elements: (template.elements ?? []).map((el) => sanitizeElement(el as PlaceCardElement)),
    layout: template.layout ?? { foldLinePosition: 0.5, showFoldLine: true, mirrorByDefault: false },
  };

  const entries = Object.entries(cleanTemplate).filter(([, value]) => value !== undefined);
  return Object.fromEntries(entries);
}

